/*
 * The Southern Air Import — readable, cover-shaped pages.
 *
 * Merchant (2026-10-30), in order:
 *   "it should fit the shape of the normal magazine ... the aspect ratio of
 *    the covers"; "more pages but also less items per page ... the size of
 *    the page should be the exact same as the covers and ads"; "I mean just
 *    have a few items per page and fill the page fully, text should be
 *    readable and magnifier should be off by default".
 *
 * Six parts, all in this one file so the large hand-tuned files
 * (assets/sat-issue-plan-patch.js, assets/sat-import-pager-patch.js,
 * snippets/sat-import.liquid) stay untouched:
 *
 * 1. Pagination. Rewrites window.SAT_ISSUE.pages (the curated plan both of
 *    those patches read live on every render). The story pages are spread
 *    through the issue at fixed slots instead of running back to back after
 *    Page 1, and a renderVals wrapper moves the pager's per-page story flags
 *    to match (the pager pins each story to a spread index). Every curated
 *    item then runs 4-7 to a page on "continued" pages, so all of them still
 *    print. A size of 0 is passed as a Number object because the pager reads
 *    `def.size || 9` — a plain 0 would silently become 9.
 *
 *    (Part 3, the rate-card paste-up, replaced part 2's two-column card grid
 *    on every page that sells items; part 2 is still the fallback.)
 *
 * 2. Readable layout (desktop, > 900px). Every page and ad is laid out at
 *    640px wide on a cover-shaped (1024:1536) sheet, two columns, with the
 *    same fixed, legible type sizes the phone layout uses (scaled up a step),
 *    instead of a 951px three-column page shrunk to fit. Measured live:
 *    with the counts above every page comes out at 860-1,030px against a
 *    960px sheet; anything over is shrunk the last few percent inside the
 *    sheet so the shape never changes. To fit at that size Page 1 drops its
 *    Collective Spotlight / Reader Mail / Dealers / Latest Story boxes and
 *    the closing page shows only its wire report (its classifieds, index and
 *    single item are hidden) — desktop only; phones show everything.
 *
 * 3. Rate-card paste-up (desktop). Each page that sells items is cut into
 *    late-80s rate-card units on a 3-column, 4-module grid and filled to the
 *    same foot line, its story or brief standing as an editorial column at
 *    the gutter — see the comment at the top of that section.
 *
 * 4. Screen fit. Each sheet (covers included) is zoomed to the full window
 *    height and scrolled to the top, so every sheet is the same on-screen
 *    size. Prev/Next bars are hidden on desktop; the fixed side arrows and
 *    the arrow keys turn pages.
 *
 * 5. Magnifier (desktop with a mouse), OFF by default; a corner button turns
 *    it on (remembered per browser). Hovering then enlarges the part under
 *    the pointer inside the same frame.
 *
 * 6. Page 1 as a contents page (desktop), set like Soldier of Fortune's:
 *    the features in two ruled columns with their page numbers (read from
 *    the pager's own index), the cover beside them with its caption, the
 *    department filter and search under it, the ordering terms at the foot.
 *    Page 1 sells nothing; its two items run in the Supply Locker.
 *
 * Zoom values are reasserted every animation frame because sat-runtime.js
 * rewrites each element's whole style attribute on every render (see
 * assets/sat-import-page-fit-patch.js for that history).
 */
(function () {
  'use strict';

  /* ---------- 1. Pagination ---------- */

  // Merchant follow-ups: "each page has between 5-7 items"; then the rate-card
  // rework (section 3); then "almost every page after page 11 is perfect ...
  // the ones before it are kinda janky mainly due to the articles being forced
  // upfront". So the seven story pages no longer run back to back at the
  // front of the book: each has its own slot through the issue (a story
  // about every third page, the way SOF spread its features through the
  // book), and on its page the story itself is the editorial column the ads
  // stack beside (section 3). Their wire briefs move to the continued pages,
  // one per page, where they run as gutter columns too.
  var STORY_KEYS = [null, 'hasenfus', 'k219', 'mammoth', 'reykjavik', 'vanunu', 'taxreform', 'blade']; // plan indices 0-7
  // Page 1 (index 0) is the contents page and sells nothing: its two items
  // join the continued pages' pool like every other page's picks.
  // November 1986 issue (merchant, 2026-10-30): the Reykjavik slot now runs
  // Feature I, the Advanced Tactical Fighter, as a full page like Feature II;
  // the two long sidebar stories (Star Wars, Air Defense) sell three items.
  var STORY_SIZES = [0, 0, 3, 0, 0, 3, 4, 4];   // items sold beside each (0 = the story fills its page)
  // middle-spread index each story is laid at (0 = page 3, after Oscar4Fox)
  var STORY_SLOTS = { blade: 1, k219: 2, tomcat: 3, tomcat2: 4, hasenfus: 5, vanunu: 8, mammoth: 11, reykjavik: 14, taxreform: 17, afghan: 20, afghan2: 22, gulf: 24, gulf2: 26 };
  // Stories the issue plan has no page for (merchant's November 1986
  // international features), with the items each page sells: each feature
  // opens on a full page of its own and finishes, a page or two later, as a
  // column beside some items ("Continued on page N" / "from page N").
  // The cover story ("The Super Tomcat", the F-14A Plus on the cover) runs as
  // a two-page spread right after the first departments.
  // The merchant's mail-order ads run large on two ad pages of their own
  // after the Gulf feature (inside the stories they crowded the articles).
  var EXTRA_STORIES = { tomcat: 0, tomcat2: 0, afghan: 0, afghan2: 3, gulf: 0, gulf2: 0 };
  var CONT_OF = { tomcat2: 'tomcat', afghan2: 'afghan', gulf2: 'gulf' };
  var KEEP_BRIEFS = { blade: 1 };               // the Blade Room keeps its department intro
  var BRIEF_PAGE_SIZE = 5;                      // items on a continued page that also runs a brief
  var LONG_BRIEF = 650;                         // characters; a longer brief needs a deeper column, so one item fewer
  // The November issue's Supply Locker briefs (merchant: "replace the supply
  // lockers briefs to the new ones"). They replace every brief the issue
  // plan carries; the plan file itself is left as it is.
  // A brief's photo rides on its card as it.photo (snippets/sat-import.liquid
  // draws it when set); the issue plan's card builder does not pass one, so
  // the renderVals wrapper below adds it by headline.
  // Every photo in the issue is 1986 or older (merchant); none has been
  // found for the Gulfstream IV, so its brief runs without one for now.
  // A brief can also be a mail-order ad (merchant: put an ad in a product
  // page "as a grid card"): adImg stands in for the whole card, its text hidden.
  var BRIEF_PHOTOS = {
    'Support Afghan Freedom Fighters': { adImg: 'https://cdn.shopify.com/s/files/1/0920/4531/2274/files/sai-nov86-ad-afghan-ink.png?v=1793435993', adRatio: '285/596' },
    'Wear A Different Helmet': { adImg: 'https://cdn.shopify.com/s/files/1/0920/4531/2274/files/sai-nov86-ad-divers-ink.png?v=1793435993', adRatio: '270/583' },
    'They Need Your Help': { adImg: 'https://cdn.shopify.com/s/files/1/0920/4531/2274/files/sai-nov86-ad-elsalvador-ink.png?v=1793435993', adRatio: '272/578' },
    'Computer Classification System': { adImg: 'https://cdn.shopify.com/s/files/1/0920/4531/2274/files/sai-nov86-ad-dms-ink.png?v=1793435993', adRatio: '270/579' },
    'Help Win In Central America': { adImg: 'https://cdn.shopify.com/s/files/1/0920/4531/2274/files/sai-nov86-ad-refugee-ink.png?v=1793435993', adRatio: '272/579' },
    'Some Hot Tips For 9mm Shooters': { adImg: 'https://cdn.shopify.com/s/files/1/0920/4531/2274/files/sai-nov86-ad-uzi-ink.png?v=1793435994', adRatio: '958/671' }
  };
  var NEW_BRIEFS = [
    { pos: 'top', rot: '-.4deg', kicker: 'Aircraft & Executive Aviation',
      head: 'The Gulfstream IV',
      dek: 'A New Generation Of Executive Transport Takes Shape',
      paras: [
        'The executive aviation market continues to demand greater range, improved cabin comfort and increasingly sophisticated flight instrumentation. Gulfstream Aerospace\u2019s new Gulfstream IV represents a substantial development in the company\u2019s long-range business aircraft family.',
        'The aircraft builds upon the established Gulfstream tradition while introducing new engines, an advanced cockpit arrangement and improvements to the cabin.',
        'The most noticeable changes are found in the flight deck. Modern electronic displays are intended to reduce the workload associated with traditional instrument scanning while providing pilots with a more integrated presentation of flight information.',
        'The aircraft also benefits from improved propulsion and aerodynamic refinements. Together, these developments are intended to provide greater operational efficiency and an expanded performance envelope.',
        'For international businesses, the appeal of such aircraft is straightforward. Direct transportation between distant commercial centers can save considerable time, while the ability to operate independently of scheduled airline services provides flexibility that conventional passenger transport cannot always offer.',
        'The Gulfstream IV also illustrates the continuing expansion of the corporate aircraft market. Executive aviation is no longer limited to short regional journeys. Long-range aircraft are increasingly being considered essential transportation assets by companies with international operations.',
        'For charter operators and aircraft management firms, the arrival of more capable executive aircraft will create opportunities in both private transportation and specialized commercial services.',
        'IMPORT NOTE: Aircraft of this class represent a significant capital investment. Operators should evaluate maintenance support, engine servicing arrangements and resale prospects alongside the aircraft\u2019s advertised performance.'
      ],
      foot: 'NOV. 10, 1986 \u00b7 AVIATION DESK \u00b7 DEPT. 9' },
    // the merchant's mail-order ads, each a card in a Supply Locker grid
    // (late: true spreads them through the back of the book)
    { pos: 'top', rot: '0deg', kicker: 'Advertisement', late: true, head: 'Support Afghan Freedom Fighters', dek: '', paras: [], foot: '' },
    { pos: 'top', rot: '0deg', kicker: 'Advertisement', late: true, head: 'Wear A Different Helmet', dek: '', paras: [], foot: '' },
    { pos: 'top', rot: '0deg', kicker: 'Advertisement', late: true, head: 'They Need Your Help', dek: '', paras: [], foot: '' },
    { pos: 'top', rot: '0deg', kicker: 'Advertisement', late: true, head: 'Computer Classification System', dek: '', paras: [], foot: '' },
    { pos: 'top', rot: '0deg', kicker: 'Advertisement', late: true, head: 'Help Win In Central America', dek: '', paras: [], foot: '' },
    // (the wide UZI ad is left out: in a narrow grid card it read too small)
    // the issue's closing note runs on the last Supply Locker page (last: true)
    { pos: 'top', rot: '.3deg', kicker: 'Editorial', last: true,
      head: 'The Import Desk',
      dek: 'November 1986 \u2014 Editorial Closing Note',
      paras: [
        'The developments reported in this issue illustrate an increasingly interconnected international market.',
        'Aircraft manufacturers are pursuing new technologies, governments are expanding defense research partnerships, and the business of military procurement is becoming more complex.',
        'For the independent supplier, the opportunities remain substantial, but the requirements are changing. Reliable documentation, dependable transport arrangements and a clear understanding of international regulations are becoming just as important as the equipment itself.',
        'The Import will continue to follow these developments, with particular attention to the aircraft, equipment and industrial services shaping the international marketplace.'
      ],
      foot: 'SOUTHERN AIR TRADE \u00b7 INTERNATIONAL PROCUREMENT \u00b7 AVIATION \u00b7 LOGISTICS' }
  ];
  var RHYTHM = [6, 7, 6, 5, 7, 6, 5];           // items per plain continued page; uneven, like a real back of book
  var storyAt = [];                             // middle-spread index -> story key, set by paginate()

  function briefChars(b) {
    return [b.kicker, b.head, b.dek, (b.paras || []).join(' '), b.foot].join(' ').length;
  }

  // Page counts that follow RHYTHM and add up to exactly `left`, every page 5-7.
  function rhythmSizes(left) {
    if (left <= 0) return [];
    var k = Math.max(1, Math.round(left / 6));
    var sizes = [];
    for (var i = 0; i < k; i++) sizes.push(RHYTHM[i % RHYTHM.length]);
    var sum = sizes.reduce(function (a, b) { return a + b; }, 0);
    var guard = 200;
    while (sum !== left && guard-- > 0) {
      var step = sum < left ? 1 : -1;
      var j;
      for (j = sizes.length - 1; j >= 0; j--) {
        if (step > 0 ? sizes[j] < 7 : sizes[j] > 5) break;
      }
      if (j < 0) { // every page already at the limit: add or drop a page
        if (step > 0) sizes.push(Math.min(7, left - sum)); else sizes.pop();
      } else {
        sizes[j] += step;
      }
      sum = sizes.reduce(function (a, b) { return a + b; }, 0);
    }
    return sizes;
  }

  // Merchant: "reset what items are in the import and try to focus on
  // vintage items and books to make it feel right". The continued pages draw
  // the pool in order, so books and period surplus go to the front (with the
  // period pieces among the alternates brought in) and modern tactical kit
  // to the back of the book. Nothing is dropped. Judged by product handle.
  var BOOK_RE = /hardcover|paperback|novel|handbook|field-manual|-book/;
  var PERIOD_RE = /east-german|west-german|soviet|ussr|swiss|swedish|polish|romanian|austrian|french|dutch|greek|spanish|israeli|german-|vietnam|m65|m56|usgi|us-gi|alice|cold-war|fragment|patch|coin|vintage|wool|ike-jacket|aviator|flyers|b-9b|ma-1|kneeboard|canteen|veshmeshok|badges|shoulder-boards|rpg-7|woobie|shemagh|duffel|chronograph|stratofreighter|blackbird/;
  var MODERN_RE = /level-iv|level-iiia|ballistic|plate|carrier|multicam|spec-ops|molle|mil-blox|legendary-series|resident-evil|fallout|metal-gear|silkies|vogue|nvg|night-vision|pvs-14|modlite|trekker|self-inflating|titanlight|botas|blackhawk|securedraw|streamlight|nanospark|mojab|hammock|dagr|hoodie|sweatshirt|respirator|tk-\d|kore-aviation|ocp|submachine|holster|battle-belt|gas-mask-pouch|bullet-proof/;
  function periodScore(h) {
    if (MODERN_RE.test(h)) return -1;
    return BOOK_RE.test(h) ? 2 : PERIOD_RE.test(h) ? 1 : 0;
  }
  function byPeriod(picks, alternates) {
    var seen = {};
    var all = picks.concat((alternates || []).filter(function (h) { return periodScore(h) > 0; }));
    var list = [];
    all.forEach(function (h, i) { if (!seen[h]) { seen[h] = 1; list.push({ h: h, s: periodScore(h), i: i }); } });
    list.sort(function (a, b) { return (b.s > 0 ? 1 : b.s) - (a.s > 0 ? 1 : a.s) || a.i - b.i; });
    return list.map(function (x) { return x.h; });
  }

  function paginate() {
    var issue = window.SAT_ISSUE;
    if (!issue || !Array.isArray(issue.pages) || issue._sheeted) return;
    var orig = issue.pages.slice();
    if (orig.length !== 9) return; // plan changed shape — leave it alone rather than guess

    var first = null;
    var stories = {};
    var carried = [];
    var reserved = {};
    STORY_SIZES.forEach(function (n, i) {
      var key = STORY_KEYS[i];
      var def = Object.assign({}, orig[i]);
      def.size = n === 0 ? new Number(0) : n; // eslint-disable-line no-new-wrappers
      if (i === 0) { def.gridSize = def.size; } else { delete def.gridSize; }
      var briefs = [];   // the plan's own briefs give way to NEW_BRIEFS
      var keep = key ? (KEEP_BRIEFS[key] || 0) : briefs.length;
      carried = carried.concat(briefs.slice(keep));
      def.briefs = briefs.slice(0, keep);
      // A story page now comes after some continued pages, and those draw on
      // one shared pool of every page's picks: hold the picks a story page
      // sells back to the end of that pool so its curated items stay with it.
      if (key && n > 0) (def.picks || []).slice(0, n + 2).forEach(function (h) { reserved[h] = 1; });
      if (i === 0) { first = def; } else { stories[key] = def; }
    });

    carried = NEW_BRIEFS.slice();
    var pool = { name: 'Supply Locker (continued)', picks: [], alternates: [] };
    var held = [];
    var total = 0;
    var inStories = STORY_SIZES.reduce(function (a, b) { return a + b; }, 0);
    orig.forEach(function (o) {
      (o.picks || []).forEach(function (h) { (reserved[h] ? held : pool.picks).push(h); });
      pool.alternates = pool.alternates.concat(o.alternates || []);
      total += o.size || 0;
    });
    pool.picks = byPeriod(pool.picks, pool.alternates);
    // each ad card takes the room of one modern listing, dropped from the back
    var adCards = NEW_BRIEFS.filter(function (b) { return b.late; }).length;
    for (var q = pool.picks.length - 1, cut = 0; q >= 0 && cut < adCards; q--) {
      if (periodScore(pool.picks[q]) < 0) { pool.picks.splice(q, 1); cut++; }
    }
    pool.picks = pool.picks.concat(held);

    total -= cut;
    var inExtras = Object.keys(EXTRA_STORIES).reduce(function (a, k) { return a + EXTRA_STORIES[k]; }, 0);
    var left = Math.max(0, total - inStories - inExtras);
    var briefPages = [];
    for (var k = 0; k < carried.length && left > 0; k++) {
      var n = Math.min(briefChars(carried[k]) > LONG_BRIEF ? BRIEF_PAGE_SIZE - 1 : BRIEF_PAGE_SIZE, left);
      briefPages.push(Object.assign({}, pool, { size: n, briefs: [carried[k]] }));
      left -= n;
    }
    var plainPages = rhythmSizes(left).map(function (m) {
      return Object.assign({}, pool, { size: m, briefs: [] });
    });
    // brief pages first, around the stories; the back of the book stays
    // plain mail-order pages, as in SOF
    var tail = briefPages.filter(function (bp) { return bp.briefs[0].last; });
    var late = briefPages.filter(function (bp) { return bp.briefs[0].late; });
    // the ad pages go evenly between the plain pages, never two together
    var mixed = [];
    var gap = Math.max(1, Math.floor(plainPages.length / (late.length + 1)));
    plainPages.forEach(function (pp, n) {
      mixed.push(pp);
      if ((n + 1) % gap === 0 && late.length) mixed.push(late.shift());
    });
    var conts = briefPages.filter(function (bp) { return !bp.briefs[0].last && !bp.briefs[0].late; })
      .concat(mixed, late, tail);

    // stories at their slots (the next free one on a clash), continued pages
    // in order around them
    Object.keys(EXTRA_STORIES).forEach(function (key) {
      var n = EXTRA_STORIES[key];
      // a continuation draws its items from the shared pool like any continued page
      stories[key] = Object.assign({}, pool, { size: n === 0 ? new Number(0) : n, briefs: [] }); // eslint-disable-line no-new-wrappers
    });
    var keys = Object.keys(STORY_SLOTS).filter(function (key) { return stories[key]; });
    var nMid = keys.length + conts.length;
    var middle = [];
    storyAt = [];
    keys.sort(function (x, y) { return STORY_SLOTS[x] - STORY_SLOTS[y]; }).forEach(function (key) {
      var at = Math.min(STORY_SLOTS[key], nMid - 1);
      while (at < nMid && middle[at]) at++;
      while (at >= nMid || (at >= 0 && middle[at])) at--;
      middle[at] = stories[key];
      storyAt[at] = key;
    });
    Object.keys(CONT_OF).forEach(function (k2) {
      var i2 = storyAt.indexOf(k2);
      var i1 = storyAt.indexOf(CONT_OF[k2]);
      if (i1 >= 0 && i2 >= 0 && i2 < i1) {
        var d = middle[i1]; middle[i1] = middle[i2]; middle[i2] = d;
        storyAt[i1] = k2; storyAt[i2] = CONT_OF[k2];
      }
    });
    for (var m = 0; m < nMid; m++) if (!middle[m]) middle[m] = conts.shift();

    var closing = Object.assign({}, orig[orig.length - 1]);
    closing.size = 1;

    issue.pages = [first].concat(middle, [closing]);
    issue.storyAt = storyAt.slice();
    issue._sheeted = true;
    // the app may already have rendered once from the original plan
    if (window.SATApp && typeof window.SATApp.setState === 'function') window.SATApp.setState({});
  }

  // assets/sat-import-pager-patch.js pins each story to a fixed middle-spread
  // index (Hasenfus 0, K-219 1, Mammoth 2, ...). The template draws a story on
  // whichever page object carries its show* flag, so re-point the flags at
  // the slots paginate() chose. K-219 and Vanunu are drawn in the page's
  // sidebar, which exists only while hasSideStory is set. The issue index
  // the pager builds is renamed to match, and read back for the contents on
  // Page 1 (section 6).
  var STORY_FLAGS = { hasenfus: 'showHasenfus', k219: 'showK219', mammoth: 'showMammoth', reykjavik: 'showReykjavik', vanunu: 'showVanunu', taxreform: 'showTaxReform', afghan: 'showAfghan', afghan2: 'showAfghan2', gulf: 'showGulf', gulf2: 'showGulf2', tomcat: 'showTomcat', tomcat2: 'showTomcat2', classified: 'showClassified', ads1: 'showAds1', ads2: 'showAds2' };
  var INDEX_NAMES = { hasenfus: 'Washington\u2019s Secret Ledger', k219: 'Star Wars Goes International', mammoth: 'Mammoth Lakes Feature', reykjavik: 'Air Superiority Feature', vanunu: 'The Future Of Air Defense', taxreform: 'The 386 Revolution', blade: 'The Blade Room', afghan: 'Afghanistan Feature', afghan2: 'Afghanistan Feature (cont.)', gulf: 'Gulf Tanker War Feature', gulf2: 'Gulf Tanker War Feature (cont.)', tomcat: 'The Super Tomcat', tomcat2: 'The Super Tomcat (cont.)', classified: 'Classified Exchange', ads1: 'Advertisements', ads2: 'Reader Appeals' };
  var PAGER_NAMES = { 'Mammoth Lakes Feature': 1, 'Southern Air Transport': 1, 'K-219 Bulletin': 1, 'Reykjavik Dispatch': 1, 'Vanunu Wire Brief': 1, 'Washington Wire': 1, 'Dept. 9 Supply Locker': 1 };
  var storyPage = {};     // story key -> interior page number
  var lockerPages = [];   // interior page numbers of the plain Supply Locker spreads

  // Page-turn sound (merchant, 2026-10-31: "make it so one of them plays at
  // random when turning the page"). assets/sat-import-page-turn.mp3 holds six
  // recorded turns; each turn plays one of them (never the same one twice
  // running), cut out of the file with a short fade at either end. The file
  // is served beside this script, so it loads same-origin.
  var TURN_CLIPS = [[0.25, 0.78], [1.38, 0.78], [2.35, 0.82], [3.31, 0.82], [4.30, 0.60], [5.05, 0.58]]; // [start, length] s
  // Merchant: "make the page turn way quieter its very annoying so make sure
  // a mute button is avil". Quiet by default; the "Sound: On/Off" button
  // beside Print This Issue mutes it, remembered on this browser.
  var TURN_VOLUME = 0.18;
  var MUTE_KEY = 'sai-turn-sound-muted';
  var turnSnd = { ctx: null, buf: null, last: -1, muted: false };
  try { turnSnd.muted = window.localStorage.getItem(MUTE_KEY) === '1'; } catch (e) { /* storage blocked: sound stays on */ }
  function showMute() {
    document.documentElement.setAttribute('data-sai-muted', turnSnd.muted ? '1' : '0');
  }
  showMute();
  var muteCss = document.createElement('style');
  muteCss.setAttribute('data-m', 'sai-sound-css');
  muteCss.textContent =
    'html[data-sai-muted="1"] [data-m="sai-sound-on"], html:not([data-sai-muted="1"]) [data-m="sai-sound-off"] { display: none; }';
  (document.head || document.documentElement).appendChild(muteCss);
  // The page-edge tab ("SUPPLY LOCKER 12" down the left side) drops its
  // black band (merchant: "remove the black/dark grey ... it takes away from
  // the art"); the label stays, printed in faded ink on the paper.
  var tabCss = document.createElement('style');
  tabCss.setAttribute('data-m', 'sai-tab-css');
  tabCss.textContent =
    '#store [data-m="sai-tab"], #store div[style*="width:26px"][style*="background:#141412"] { background: transparent !important; }' +
    '#store div[style*="width:26px"][style*="background:#141412"] > span,' +
    '#store [data-m="sai-tab"] > span { color: rgba(20,20,18,.5) !important; text-shadow: none !important; }' +
    // Product photos printed on the paper, like the Magnum ad (merchant:
    // "give the actual product listings the treatment we gave the magnum
    // ad", then "some to black and white and some to color"). The shot
    // multiplies onto the page, so its white background becomes the paper.
    // A blend only reaches the paper with nothing isolating it on the way:
    // the photo box's slight tilt and the card's uneven ink opacity both
    // did, so they go. Two cards in five print black and white (never the
    // featured one); the rest keep their colour under a light period tone.
    '#store [data-m="sai-ad"] { opacity: 1 !important; }' +
    // the F-14 cover story prints onto the paper rather than sitting over it
    // (merchant: "looks overlayed"): the spread multiplies onto the page, so
    // the grain and stains show through its photos and dark panels
    '#store [data-m="sai-page"]:has(img[data-m^="sai-article-tomcat"]) [data-m="sai-inner"] { mix-blend-mode: multiply; }' +
    '#store img[data-m^="sai-article-tomcat"] { filter: sepia(.22) saturate(.8) contrast(1.04) !important; }' +
    // no frame round a product shot either: the box it sits in drops its rule
    '#store [data-m="sai-ad-photo"], #store div:has(> sat-photo[placeholder="Product photo"]) { border: 0 !important; }' +
    // an ad brief card: only the ad, no plate, no text
    '#store div:has(> img[data-m="sai-brief-ad"]) { padding: 0 !important; justify-content: center !important; align-items: center !important; height: 100% !important; }' +
    '#store div:has(> img[data-m="sai-brief-ad"]) > :not(img) { display: none !important; }' +
    'html body #store [data-m="sai-page"] [data-m="sai-ad"][data-m]:has(img[data-m="sai-brief-ad"]), #store [data-m="sai-ad"]:has(img[data-m="sai-brief-ad"]) { border: 0 !important; box-shadow: none !important; background: transparent !important; transform: none !important; }' +
    // nothing over or around a photo (merchant: "no image should ever have
    // that extra stuff"): no halftone screen, no grey plate
    '#store [data-m="sai-ad-photo"] > span { display: none !important; }' +
    // full-page ads: no dark plate behind them and no label over them
    '#store div:has(> img[src*="fullpage-ad"]), #store div:has(> img[src*="house-ad"]), #store div:has(> img[src*="hk-vp70z"]) { background: transparent !important; }' +
    '#store [data-m="sai-page"]:has(img[src*="fullpage-ad"]) > span, #store [data-m="sai-page"]:has(img[src*="house-ad"]) > span, #store [data-m="sai-page"]:has(img[src*="hk-vp70z"]) > span { display: none !important; }' +
    '#store [data-m="sai-ad-photo"], #store div:has(> sat-photo[placeholder="Product photo"]) { transform: none !important; background: transparent !important; }' +
    '#store sat-photo[placeholder="Product photo"] { mix-blend-mode: multiply !important; filter: sepia(.16) saturate(.9) contrast(1.06) brightness(1.03) !important; }' +
    '#store [data-m="sai-ad"]:not([data-sai-hero]):nth-child(5n+2) sat-photo[placeholder="Product photo"],' +
    '#store [data-m="sai-ad"]:not([data-sai-hero]):nth-child(5n+4) sat-photo[placeholder="Product photo"] { filter: grayscale(1) contrast(1.15) brightness(1.03) !important; }';
  (document.head || document.documentElement).appendChild(tabCss);
  // The theme letterboxes product shots (object-fit: contain inside the
  // photo's shadow root); with no plate behind them that left bare paper
  // round the product (merchant: "this annoying extra space ... crop that").
  // Crop to fill instead; the shadow root is out of the stylesheet's reach.
  setInterval(function () {
    var ps = document.querySelectorAll('#store sat-photo[placeholder="Product photo"]');
    for (var i = 0; i < ps.length; i++) {
      var im = ps[i].shadowRoot && ps[i].shadowRoot.querySelector('img');
      if (im && im.style.objectFit !== 'cover') im.style.objectFit = 'cover';
    }
  }, 400);
  function toggleMute(e) {
    var b = e.target && e.target.closest && e.target.closest('[data-m="sai-sound-btn"]');
    if (!b) return;
    if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    e.stopPropagation(); // arrow-key page turning listens on the document too
    turnSnd.muted = !turnSnd.muted;
    try { window.localStorage.setItem(MUTE_KEY, turnSnd.muted ? '1' : '0'); } catch (err) { /* ignore */ }
    showMute();
  }
  document.addEventListener('click', toggleMute, true);
  document.addEventListener('keydown', toggleMute, true);
  function loadTurnSound() {
    var AC = window.AudioContext || window.webkitAudioContext;
    var me = document.querySelector('script[src*="sat-import-screen-fit-patch"]');
    if (turnSnd.ctx || !AC || !me || !window.fetch) return;
    turnSnd.ctx = new AC();
    fetch(me.src.replace(/sat-import-screen-fit-patch\.js.*$/, 'sat-import-page-turn.mp3'))
      .then(function (r) { return r.arrayBuffer(); })
      .then(function (ab) { return new Promise(function (ok, no) { turnSnd.ctx.decodeAudioData(ab, ok, no); }); })
      .then(function (b) { turnSnd.buf = b; })
      .catch(function () {});
  }
  // the browser only lets sound start after the visitor has clicked or typed
  function wakeTurnSound() {
    loadTurnSound();
    if (turnSnd.ctx && turnSnd.ctx.state === 'suspended') turnSnd.ctx.resume();
  }
  ['pointerdown', 'keydown', 'touchstart'].forEach(function (ev) {
    document.addEventListener(ev, wakeTurnSound, { capture: true, passive: true });
  });
  function playTurnSound() {
    var a = turnSnd;
    if (a.muted || !a.ctx || !a.buf) return;
    if (a.ctx.state === 'suspended') a.ctx.resume();
    var i = Math.floor(Math.random() * TURN_CLIPS.length);
    if (i === a.last) i = (i + 1 + Math.floor(Math.random() * (TURN_CLIPS.length - 1))) % TURN_CLIPS.length;
    a.last = i;
    var c = TURN_CLIPS[i];
    var t = a.ctx.currentTime;
    var src = a.ctx.createBufferSource();
    var g = a.ctx.createGain();
    src.buffer = a.buf;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(TURN_VOLUME, t + 0.015);
    g.gain.setValueAtTime(TURN_VOLUME, t + c[1] - 0.08);
    g.gain.linearRampToValueAtTime(0, t + c[1]);
    src.connect(g);
    g.connect(a.ctx.destination);
    src.start(t, c[0], c[1]);
  }
  if (document.readyState === 'complete') setTimeout(loadTurnSound, 1200);
  else window.addEventListener('load', function () { setTimeout(loadTurnSound, 1200); });

  var lastTurnPage = null;
  function placeStories() {
    var proto = window.SATComponent && window.SATComponent.prototype;
    if (!proto || proto._saiStorySlots || typeof proto.renderVals !== 'function') return;
    var base = proto.renderVals;
    proto.renderVals = function () {
      // the turn's direction is known here, before the new sheet is drawn,
      // so its slide starts the right way from the first frame (section 2)
      var st = (this && this.state) || (window.SATApp && window.SATApp.state) || {};
      var pageNo = +st.saiInteriorPage || 0;
      if (lastTurnPage !== null && pageNo !== lastTurnPage) {
        document.documentElement.setAttribute('data-sai-dir', pageNo < lastTurnPage ? 'prev' : 'next');
        playTurnSound();
      }
      lastTurnPage = pageNo;
      var out = base.apply(this, arguments);
      if (!storyAt.length || !Array.isArray(out.saiMiddlePages)) return out;
      out.saiMiddlePages = out.saiMiddlePages.map(function (pg, idx) {
        var key = storyAt[idx];
        var o = Object.assign({}, pg);
        Object.keys(STORY_FLAGS).forEach(function (k) { o[STORY_FLAGS[k]] = key === k; });
        var side = key === 'k219' || key === 'vanunu';
        o.hasSideStory = side;
        o.midBodyCols = side ? '230px minmax(0,1fr)' : 'minmax(0,1fr)';
        // the pager only overflows items into a sidebar on its own two side
        // indices, and only past 9 items; no page here sells more than 7
        if (!side) o.sidebarFiller = [];
        // the "Continued on / from page N" lines of a split feature
        Object.keys(CONT_OF).forEach(function (k2) {
          if (key === CONT_OF[k2]) o.contNext = storyPage[k2] || '';
          if (key === k2) o.contPrev = storyPage[CONT_OF[k2]] || '';
        });
        if (Array.isArray(o.items)) {
          o.items = o.items.map(function (it) {
            var ph = it && it.isStory && BRIEF_PHOTOS[it.head];
            return ph ? Object.assign({}, it, ph) : it;
          });
        }
        return o;
      });
      if (Array.isArray(out.saiIssueIndex)) {
        var m = 0;
        var lockers = [];
        out.saiIssueIndex = out.saiIssueIndex.map(function (e) {
          if (!e || !PAGER_NAMES[e.name]) return e;
          var key = storyAt[m++];
          if (key) { storyPage[key] = e.page; } else { lockers.push(e.page); }
          return Object.assign({}, e, { name: key ? INDEX_NAMES[key] : 'Dept. 9 Supply Locker' });
        });
        lockerPages = lockers;
      }
      return out;
    };
    proto._saiStorySlots = true;
  }
  placeStories();
  paginate();

  if (!('zoom' in document.documentElement.style)) { return; }

  /* ---------- 2. Readable cover-shaped layout ---------- */

  var P = '#store [data-m="sai-page"]';
  var P1 = P + ':has([data-m2="sai-cols"])';
  var PC = P + ':has([data-m="sai-back"]) [data-m="sai-inner"] > ';
  var SB = P + ':not(:has([data-m2="sai-cols"])) [data-m="sai-sidebar"]';
  var css = document.createElement('style');
  css.setAttribute('data-m', 'sai-sheet-css');
  css.textContent =
    '@media (min-width: 901px) {' +
    P + ' { width: 640px !important; max-width: 640px !important; aspect-ratio: 1024 / 1536 !important; overflow: hidden !important; }' +
    P + ' > img { width: 100% !important; height: 100% !important; object-fit: cover !important; }' +
    P + ' [data-m="sai-body"] { display: block !important; }' +
    P + ' [data-m="sai-sidebar"] { display: block !important; column-count: 2; column-gap: 14px; margin-top: 14px; }' +
    P + ' [data-m="sai-sidebar"] > * { break-inside: avoid; margin-bottom: 12px; }' +
    // Every card (the old full-width "hero" included) sits in one 2-column grid
    // in reading order: the grid wrapper dissolves (display:contents) so its
    // cards and the hero become cells of the same parent; anything else in
    // that parent (stories, headers) spans both columns.
    P + ' :has(> [data-m="sai-grid"]) { display: grid !important; grid-template-columns: 1fr 1fr !important; align-items: stretch; }' +
    P + ' :has(> [data-m="sai-grid"]) > :not([data-m="sai-ad"]):not([data-m="sai-grid"]) { grid-column: 1 / -1; }' +
    P + ' [data-m="sai-grid"] { display: contents !important; }' +
    P + ' [data-m="sai-ad"] { transform: none !important; margin: 0 !important; column-span: none !important; grid-column: auto !important; }' +
    // compact card: fixed photo, 2-line description, no checklist/coupon/stock-number/shipping lines
    P + ' [data-m="sai-ad-body"] { flex-direction: column !important; }' +
    P + ' [data-m="sai-ad-photo"] { width: 100% !important; flex: none !important; height: 110px !important; min-height: 110px !important; max-height: 110px !important; }' +
    P + ' [data-m="sai-ad-head"] { font-size: 15px !important; line-height: 1.1 !important; }' +
    P + ' [data-m="sai-ad-blurb"] { font-size: 12px !important; line-height: 1.4 !important; display: -webkit-box !important; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden !important; }' +
    P + ' [data-m="sai-ad-price"] { font-size: 19px !important; }' +
    P + ' [data-m="sai-ad-checks"], ' + P + ' [data-m="sai-ad-coupon"] { display: none !important; }' +
    P + ' div:has(> div > [data-m="sai-ad-price"]) > div:first-child:not(:has([data-m="sai-ad-price"])), ' +
    P + ' div:has(> div > [data-m="sai-ad-price"]) > div:last-child:not(:has([data-m="sai-ad-price"])) { display: none !important; }' +
    // Mixed box sizes (merchant: "some can be larger and others smaller but
    // should make sense"). assignSizes() below tags each card with
    // data-sai-size: F = featured (full width, photo beside text), S =
    // standard, T = tall standard (spans two small rows), C = small (photo
    // beside name and price, no description).
    P + ' :has(> [data-m="sai-grid"]) { grid-auto-flow: row dense !important; }' +
    P + ' [data-sai-size="F"] { grid-column: 1 / -1 !important; }' +
    P + ' [data-sai-size="F"] [data-m="sai-ad-body"] { flex-direction: row !important; gap: 12px; }' +
    P + ' [data-sai-size="F"] [data-m="sai-ad-photo"] { width: 50% !important; height: 196px !important; min-height: 196px !important; max-height: 196px !important; }' +
    P + ' [data-sai-size="F"] [data-m="sai-ad-head"] { font-size: 21px !important; }' +
    P + ' [data-sai-size="F"] [data-m="sai-ad-blurb"] { -webkit-line-clamp: 5 !important; font-size: 12.5px !important; }' +
    P + ' [data-sai-size="F"] [data-m="sai-ad-price"] { font-size: 24px !important; }' +
    P + ' [data-sai-size="T"] { grid-row: span 2 !important; }' +
    P + ' [data-sai-size="T"] [data-m="sai-ad-photo"] { height: 170px !important; min-height: 170px !important; max-height: 170px !important; }' +
    P + ' [data-sai-size="T"] [data-m="sai-ad-blurb"] { -webkit-line-clamp: 4 !important; }' +
    P + ' [data-sai-size="C"] [data-m="sai-ad-body"] { flex-direction: row !important; gap: 8px; }' +
    P + ' [data-sai-size="C"] [data-m="sai-ad-photo"] { width: 36% !important; height: 92px !important; min-height: 92px !important; max-height: 92px !important; }' +
    P + ' [data-sai-size="C"] [data-m="sai-ad-head"] { font-size: 13.5px !important; }' +
    P + ' [data-sai-size="C"] [data-m="sai-ad-blurb"] { display: none !important; }' +
    P + ' [data-sai-size="C"] [data-m="sai-ad-price"] { font-size: 16px !important; }' +
    P + ' [data-m="sai-back"] { grid-template-columns: 1fr !important; }' +
    // A story page's sidebar holds a single story box: run it across the
    // head of the page with its copy in three ruled columns (an SOF news
    // brief) instead of half the width with the other half blank.
    SB + ' { column-count: 1 !important; }' +
    SB + ' > div:not([data-sai-shape]) > div:nth-child(2) > div:first-child { font-size: 21px !important; }' +
    SB + ' > div:not([data-sai-shape]) > div:nth-child(2) > div:nth-child(2) { font-size: 10.5px !important; }' +
    SB + ' > div:not([data-sai-shape]) > div:nth-child(2) > div:has(> p) { column-count: 3; column-gap: 16px; column-rule: 1px solid rgba(20,20,18,.25); font-size: 11px !important; line-height: 1.5 !important; }' +
    SB + ' > div:not([data-sai-shape]) > div:nth-child(2) > div:has(> p) > p { break-inside: avoid-column; }' +
    // The Mammoth feature sets its photo column (flex 260px) beside its text
    // column (flex 300px) only on a page wider than our 640px sheet; there
    // the text dropped under the photo and left a block of the page blank.
    P + ' div:has(> div[style*="flex:0 1 260px"]) { flex-wrap: nowrap !important; }' +
    // the Supply Locker's running header ("Continued") does not belong on a
    // page that is one feature (section 4 marks it)
    P + ' [data-sai-drop] { display: none !important; }' +
    P1 + ' [data-m="sai-sidebar"] > *:nth-child(4),' +
    P1 + ' [data-m="sai-sidebar"] > *:nth-child(5),' +
    P1 + ' [data-m="sai-sidebar"] > *:nth-child(6),' +
    P1 + ' [data-m="sai-sidebar"] > *:nth-child(8) { display: none !important; }' +
    PC + '[data-m="sai-grid"],' + PC + '[data-m="sai-ad"],' + PC + '[data-m="sai-back"],' + PC + '[data-m="sai-back"] + div { display: none !important; }' +
    '#store [data-m="sai-issue"] > div:first-child:not([data-m]) { display: none !important; }' +
    '#store [data-m="sai-issue"] > div:has(> [data-m="print-btn"]) { display: none !important; }' +
    '#store div:has(> div > img[src*="sai-cover"]) > span { display: none !important; }' +
    '#store div:has(> div > sat-photo[slot-id="sai-backcover-photo"]) { position: relative; }' +
    '#store div:has(> div > sat-photo[slot-id="sai-backcover-photo"]) > div:last-child { position: absolute !important; left: 0; right: 0; bottom: 18px; z-index: 3; }' +
    '#store div:has(> div > sat-photo[slot-id="sai-backcover-photo"]) > div:last-child > span { background: rgba(20,18,14,.82); }' +
    '#store div:has(> sat-photo[slot-id="sai-backcover-photo"]) { overflow: hidden; }' +
    '}';
  (document.head || document.documentElement).appendChild(css);

  // Page turn. Every page's inline style runs the theme's magReveal, which
  // starts at opacity 0 and (fill-mode both) holds there until it runs: the
  // bright desk showed through as a flash on every turn, and a page whose
  // animation stalled stayed invisible (merchant: "pages disappear" and "a
  // really heavy bright flash instead of a cool page turn"), then "pages
  // turn weird and the wrong way". The new sheet slides a short way in from
  // the side it is turned from (the right going forward, the left going
  // back), its leading edge shaded, and settles; it is never transparent, so
  // a stall can never hide it (section 1's renderVals wrapper sets the
  // direction on <html> before the sheet is drawn).
  var turnCss = document.createElement('style');
  turnCss.setAttribute('data-m', 'sai-turn-css');
  turnCss.textContent =
    P + ' { animation: saiTurnNext .34s cubic-bezier(.2,.7,.25,1) backwards !important; }' +
    'html[data-sai-dir="prev"] ' + P + ' { animation-name: saiTurnPrev !important; }' +
    '@keyframes saiTurnNext { 0% { transform: translateX(34px); filter: brightness(.86); box-shadow: -18px 0 26px -8px rgba(0,0,0,.45); } 100% { transform: translateX(0); filter: brightness(1); } }' +
    '@keyframes saiTurnPrev { 0% { transform: translateX(-34px); filter: brightness(.86); box-shadow: 18px 0 26px -8px rgba(0,0,0,.45); } 100% { transform: translateX(0); filter: brightness(1); } }' +
    '@media (prefers-reduced-motion: reduce) { ' + P + ' { animation: none !important; } }';
  (document.head || document.documentElement).appendChild(turnCss);

  /* ---------- 3. Rate-card paste-up ----------
   *
   * Merchant: "Scale still is weird, go look up magazines at the time and see
   * how they are organized ... create an algorithm based on those magazines
   * like Soldier of Fortune and Guns & Ammo around the late 80s."
   *
   * What the period books do (SOF Nov 1987 and May 1989 scans on the
   * Internet Archive, plus published rate cards):
   *  - The type page is three columns. Every ad is sold as a fixed fraction of
   *    it: 1/12 (one column, a quarter deep), 1/6 vertical or horizontal, 1/3
   *    square or vertical, 1/2 horizontal, 2/3 vertical, full. So a page is a
   *    3 x 4 grid of modules and every box is a whole number of modules.
   *  - Dealer catalog pages (Cutlery Shoppe "Military Blades", Guardian "Stun
   *    Guns", Greene Military Distributors) run a department banner across the
   *    head, one dominant unit, and a mosaic of smaller cells that fills the
   *    page to the foot. Every page ends on the same line.
   *  - The dominant unit sits at the head, on the outside edge; 1/12s collect
   *    toward the foot. Editorial (story jumps, department copy) runs in the
   *    column next to the gutter, ads on the outside.
   *  - Facing pages are made up differently: a hero page faces a page of tall
   *    columns, a half-page banner faces a mosaic.
   *  - Copy is fitted to the space bought, not the other way round.
   *
   * Our listings carry at most 150 characters of copy and mostly square
   * photos, so the full-depth columns (1/3 and 2/3 vertical) would print as
   * a tall empty box around a small picture; they are left off our card.
   *
   * The engine below does that for each page: it measures the depth
   * left on the sheet, picks the number of module rows that depth holds,
   * enumerates every way to cut that grid into rate-card units for the page's
   * cards, scores each cut against the rules above, gives the biggest units to
   * the biggest-ticket items, then copy-fits each card to its unit. Desktop
   * only; layouts are cached per page so a revisit looks identical.
   */

  var RC_COLS = 3;
  var RC_MIN_MODULE = 180;   // shallowest module row (sheet px) that still holds photo, name and price
  var RC_MAX_ROWS = 4;       // a full page is 4 modules deep (1 module = 1/12 page)

  // Rate-card units, in modules. h: 0 means "the full depth of the grid".
  var UNITS = [
    { u: 'U', w: 1, h: 1 },  // 1/12
    { u: 'T', w: 1, h: 2 },  // 1/6 vertical
    { u: 'W', w: 2, h: 1 },  // 1/6 horizontal
    { u: 'Q', w: 2, h: 2 },  // 1/3 square
    { u: 'S', w: 3, h: 1 },  // 1/4 horizontal strip
    { u: 'P', w: 3, h: 2 },  // 1/2 horizontal
    { u: 'F', w: 3, h: 0 }   // full page
  ];
  var BRIEF_HEIGHTS = [2, 3, 4]; // editorial column depths, in modules

  function unitDims(R) {
    var seen = {};
    var out = [];
    UNITS.forEach(function (d) {
      var h = d.h || R;
      if (h > R) return;
      var k = d.w + 'x' + h;
      if (seen[k]) return;
      seen[k] = 1;
      out.push({ u: d.u, w: d.w, h: h });
    });
    return out;
  }

  // Every exact cut of a 3 x R grid into n item units plus b editorial
  // columns, around an optional story unit `pre` ({w, h}) standing at the
  // head of the gutter. Pieces are {u, c, r, w, h}; columns count from the
  // gutter (c = 0) to the outside edge (c = 2). Cached per (R, n, b, pre).
  var tilingCache = {};
  function tilings(R, n, b, pre) {
    var key = R + ':' + n + ':' + b + ':' + (pre ? pre.w + 'x' + pre.h : '-');
    if (tilingCache[key]) return tilingCache[key];
    var dims = unitDims(R);
    var cells = RC_COLS * R;
    var grid = [];
    for (var i = 0; i < cells; i++) grid.push(-1);
    var out = [];
    var placed = [];
    var LIMIT = 20000;

    function fits(c, r, w, h) {
      if (c + w > RC_COLS || r + h > R) return false;
      for (var y = r; y < r + h; y++) for (var x = c; x < c + w; x++) if (grid[y * RC_COLS + x] !== -1) return false;
      return true;
    }
    function mark(c, r, w, h, v) {
      for (var y = r; y < r + h; y++) for (var x = c; x < c + w; x++) grid[y * RC_COLS + x] = v;
    }
    function briefOk(c, r) {
      // editorial runs down from the head, next to the gutter, unbroken
      if (r > 0) {
        var above = grid[(r - 1) * RC_COLS + c];
        if (above === -1 || !/^[BE]$/.test(placed[above].u)) return false;
      }
      if (c > 0) {
        var left = grid[r * RC_COLS + c - 1];
        if (left === -1 || !/^[BE]$/.test(placed[left].u)) return false;
      }
      return true;
    }
    function rec(free, items, briefs) {
      if (out.length >= LIMIT) return;
      if (free === 0) {
        if (items === n && briefs === b) out.push(placed.map(function (p) { return { u: p.u, c: p.c, r: p.r, w: p.w, h: p.h }; }));
        return;
      }
      var left = (n - items) + (b - briefs);
      if (left <= 0 || left > free) return;
      var idx = grid.indexOf(-1);
      var c = idx % RC_COLS;
      var r = Math.floor(idx / RC_COLS);
      if (briefs < b && briefOk(c, r)) {
        BRIEF_HEIGHTS.forEach(function (h) {
          if (h <= R && fits(c, r, 1, h)) {
            placed.push({ u: 'B', c: c, r: r, w: 1, h: h });
            mark(c, r, 1, h, placed.length - 1);
            rec(free - h, items, briefs + 1);
            mark(c, r, 1, h, -1);
            placed.pop();
          }
        });
      }
      if (items < n) {
        dims.forEach(function (d) {
          if (fits(c, r, d.w, d.h)) {
            placed.push({ u: d.u, c: c, r: r, w: d.w, h: d.h });
            mark(c, r, d.w, d.h, placed.length - 1);
            rec(free - d.w * d.h, items + 1, briefs);
            mark(c, r, d.w, d.h, -1);
            placed.pop();
          }
        });
      }
    }
    var free = cells;
    if (pre) {
      placed.push({ u: 'E', c: 0, r: 0, w: pre.w, h: pre.h });
      mark(0, 0, pre.w, pre.h, 0);
      free -= pre.w * pre.h;
    }
    rec(free, 0, 0);
    tilingCache[key] = out;
    return out;
  }

  // Small deterministic hash, so the same page always gets the same cut.
  function hash(s) {
    var h = 2166136261;
    for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return (h >>> 0) / 4294967295;
  }

  // Page families (by page number, so facing pages always differ):
  //  0 dealer hero  — Cutlery Shoppe "Military Blades": 1/3 square hero at the head
  //  1 column page  — the facing Cutlery Shoppe page / Guardian "Stun Guns": tall units
  //  2 banner page  — Greene Military Distributors: a horizontal unit across the head
  function scoreCut(t, R, n, family, need) {
    var s = 0;
    var ids = [];
    var i;
    for (i = 0; i < RC_COLS * R; i++) ids.push(-1);
    t.forEach(function (p, k) {
      for (var y = p.r; y < p.r + p.h; y++) for (var x = p.c; x < p.c + p.w; x++) ids[y * RC_COLS + x] = k;
    });
    var items = t.filter(function (p) { return p.u !== 'B' && p.u !== 'E'; });
    var areas = items.map(function (p) { return p.w * p.h; }).sort(function (a, b) { return b - a; });
    var maxA = areas[0] || 0;
    var nMax = areas.filter(function (a) { return a === maxA; }).length;
    var smalls = items.filter(function (p) { return p.u === 'U'; });

    for (i = 0; i < items.length; i++) {
      var p = items[i];
      // strips and half-pages run only across the head or the foot
      if (p.w === 3 && p.h < R && p.r !== 0 && p.r + p.h !== R) return -1e9;
    }
    // one dominant unit: two equal biggest boxes fight for the eye
    if (items.length >= 3 && maxA >= 2 && nMax > 1) s -= 16 * (nMax - 1);
    // the second unit should not dwarf the rest: keep the mosaic balanced
    if (areas.length >= 3 && maxA > 4 * (areas[1] || 1) && R >= 3) s -= 12;
    // the dominant unit: at the head, on the outside edge
    var hero = null;
    items.forEach(function (p) { if (!hero && p.w * p.h === maxA) hero = p; });
    if (hero && maxA >= 2) {
      if (hero.r === 0) s += 18;
      if (hero.c + hero.w === RC_COLS) s += 10;
    }
    // 1/12s drift toward the foot and sit together
    smalls.forEach(function (p) {
      s += 6 * (R > 1 ? p.r / (R - 1) : 1);
      smalls.forEach(function (q) {
        if (q !== p && ((q.r === p.r && Math.abs(q.c - p.c) === 1) || (q.c === p.c && Math.abs(q.r - p.r) === 1))) s += 1.5;
      });
    });
    // 1/12s hold only photo, name and price: use them sparingly (at most
    // three on a six-item page), never as a full row of three (that reads as
    // a table, not a page)
    if (smalls.length > 2) s -= 6 * (smalls.length - 2);
    if (smalls.length > Math.max(2, n - 3)) s -= 40 * (smalls.length - Math.max(2, n - 3));
    for (var row = 0; row < R; row++) {
      if (smalls.filter(function (p) { return p.r === row; }).length === RC_COLS) s -= 8;
    }
    // a page of equal boxes reads as a table: penalise four-way crossings
    for (var y = 1; y < R; y++) {
      for (var x = 1; x < RC_COLS; x++) {
        var a = ids[(y - 1) * RC_COLS + x - 1], bb = ids[(y - 1) * RC_COLS + x];
        var cc = ids[y * RC_COLS + x - 1], d = ids[y * RC_COLS + x];
        if (a !== bb && a !== cc && a !== d && bb !== cc && bb !== d && cc !== d) s -= 7;
      }
    }
    // a mix of unit shapes, like a real back-of-book page
    var kinds = {};
    items.forEach(function (p) { kinds[p.u] = 1; });
    s += 4 * Math.min(4, Object.keys(kinds).length);
    // editorial: deep enough for its copy, not padded out
    t.forEach(function (p) {
      if (p.u !== 'B') return;
      if (p.h < need) s -= 12 * (need - p.h);
      if (p.h > need) s -= 8 * (p.h - need);
      if (p.c === 0) s += 6;
    });
    // family bonuses
    if (family === 0) {
      if (hero && hero.u === 'Q' && hero.r === 0) s += 14;
    } else if (family === 1) {
      items.forEach(function (p) { if (p.h > p.w) s += 5; });
      items.forEach(function (p) { if (p.w > p.h && p.r + p.h === R) s += 3; });
    } else {
      items.forEach(function (p) { if (p.w >= 2 && p.w > p.h && p.r === 0) s += 12; });
    }
    return s;
  }

  // Best cut for a page and its score; ties broken by a per-page hash so
  // pages vary.
  function chooseCut(R, n, b, pageNo, need, pre) {
    var all = tilings(R, n, b, pre);
    if (!all.length) return null;
    var family = ((pageNo % 3) + 3) % 3;
    var best = null;
    var bestS = -1e9;
    for (var i = 0; i < all.length; i++) {
      var s = scoreCut(all[i], R, n, family, need) + 5 * hash(pageNo + ':' + i);
      if (s > bestS) { bestS = s; best = all[i]; }
    }
    return bestS > -1e8 ? { cut: best, score: bestS } : null;
  }

  var RC_GAP = 10;           // gutter between units, sheet px
  var RC_FOOT = 22;          // foot margin under the last unit, sheet px
  var SHEET_W = 640;
  var SHEET_H = 960;

  // Type per unit, sheet px: title size / max lines, copy size / max lines,
  // price size. Row units (photo beside the copy) give the photo `row`% of
  // the width. Copy-fitting only ever takes lines away from these.
  var SPEC = {
    U: { t: 15, tl: 2, b: 0, bl: 0, p: 19 },
    T: { t: 18, tl: 3, b: 12.5, bl: 6, p: 22 },
    W: { t: 18, tl: 2, b: 12.5, bl: 4, p: 22, row: 44 },
    Q: { t: 25, tl: 2, b: 13.5, bl: 4, p: 30 },
    S: { t: 21, tl: 2, b: 13, bl: 4, p: 26, row: 36 },
    P: { t: 28, tl: 2, b: 14, bl: 7, p: 34, row: 50 },
    F: { t: 34, tl: 3, b: 15, bl: 10, p: 40 }
  };
  var BRIEF_SIZES = ['11.5', '11', '10.5', '10', '9.5', '9', '8.5']; // editorial body sizes, largest first

  // Long goods want a wide unit, garments and books a tall one (product
  // photos are mostly square, so the title is the better guide).
  var WIDE_RE = /knife|kukri|bowie|blade|machete|tomahawk|chopping|duffel|sleeping bag|sleeping mat|tent|tarp|belt|harness|gun case|bomber|raptor|falcon|blackbird|stratofreighter|monocular|rover/i;
  var TALL_RE = /jacket|shirt|parka|sweater|hoodie|sweatshirt|pants|vest|carrier|boot|canteen|respirator|helmet|poncho|hardcover|paperback|novel|scarf|backpack|veshmeshok/i;

  var X = P + ' [data-sai-shape]';
  var rcRules = [
    P + ' [data-sai-rate][data-sai-rate] { display: grid !important; grid-template-columns: repeat(3, minmax(0, 1fr)) !important; column-gap: ' + RC_GAP + 'px !important; row-gap: ' + RC_GAP + 'px !important; align-items: stretch !important; align-content: stretch !important; box-sizing: border-box !important; }',
    X + ' { display: flex !important; flex-direction: column !important; min-height: 0 !important; min-width: 0 !important; height: auto !important; max-height: none !important; overflow: hidden !important; box-sizing: border-box !important; }',
    P + ' [data-sai-shape]:not([data-sai-shape="E"]) { border: 1px solid rgba(20,20,18,.8) !important; }',
    X + '[data-sai-hero] { border: 2px solid #141412 !important; }',
    X + ' > div:first-child:not(:last-child) { flex: none !important; }',
    X + ' [data-m="sai-ad-body"] { flex: 1 1 0 !important; min-height: 0 !important; display: flex !important; flex-direction: column !important; }',
    // the photo's only child is absolutely placed, so the box must be told its width
    X + ' [data-m="sai-ad-photo"] { flex: 1 1 0 !important; min-height: 44px !important; max-height: none !important; height: auto !important; width: 100% !important; align-self: stretch !important; }',
    // no plate: the product shot is printed straight onto the page's paper
    // (section 1's print rules)
    X + ' [data-m="sai-ad-photo"] { background: transparent !important; }',
    X + ' [data-m="sai-ad-photo"] > sat-photo { position: absolute !important; inset: 0 !important; width: 100% !important; height: 100% !important; }',
    X + ' [data-m="sai-ad-body"] > div:last-child { flex: none !important; min-width: 0 !important; }',
    P + ' [data-sai-clamp="0"] [data-m="sai-ad-blurb"] { display: none !important; }'
  ];
  Object.keys(SPEC).forEach(function (u) {
    var s = P + ' [data-sai-shape="' + u + '"]';
    var d = SPEC[u];
    rcRules.push(s + ' [data-m="sai-ad-head"] { font-size: ' + d.t + 'px !important; line-height: 1.08 !important; }');
    if (d.b) rcRules.push(s + ' [data-m="sai-ad-blurb"] { font-size: ' + d.b + 'px !important; line-height: 1.4 !important; }');
    rcRules.push(s + ' [data-m="sai-ad-price"] { font-size: ' + d.p + 'px !important; line-height: 1.1 !important; }');
    if (d.row) {
      rcRules.push(s + ' [data-m="sai-ad-body"] { flex-direction: row !important; gap: 12px !important; }');
      rcRules.push(s + ' [data-m="sai-ad-photo"] { flex: 0 0 ' + d.row + '% !important; width: auto !important; }');
      rcRules.push(s + ' [data-m="sai-ad-body"] > div:last-child { flex: 1 1 0 !important; overflow: hidden !important; }');
    }
  });
  for (var ck = 1; ck <= 12; ck++) {
    rcRules.push(P + ' [data-sai-clamp="' + ck + '"] [data-m="sai-ad-blurb"] { display: -webkit-box !important; -webkit-box-orient: vertical !important; -webkit-line-clamp: ' + ck + ' !important; overflow: hidden !important; }');
  }
  for (ck = 1; ck <= 3; ck++) {
    rcRules.push(P + ' [data-sai-tclamp="' + ck + '"] [data-m="sai-ad-head"] { display: -webkit-box !important; -webkit-box-orient: vertical !important; -webkit-line-clamp: ' + ck + ' !important; overflow: hidden !important; }');
  }
  // editorial columns: a readable body size, fitted per column (data-sai-bfs)
  var XB = P + ' [data-sai-shape="B"]';
  rcRules.push(XB + ' > div { flex: 1 1 auto !important; min-height: 0 !important; overflow: hidden !important; padding: 12px 13px 10px !important; }');
  rcRules.push(XB + ' > div > div:nth-child(1) { font-size: 9px !important; }');
  rcRules.push(XB + ' > div > div:nth-child(2) { font-size: 17px !important; line-height: 1.12 !important; }');
  rcRules.push(XB + ' > div > div:nth-child(3) { font-size: 8.5px !important; line-height: 1.35 !important; }');
  BRIEF_SIZES.forEach(function (fs) {
    rcRules.push(P + ' [data-sai-bfs="' + fs + '"] p { font-size: ' + fs + 'px !important; line-height: 1.46 !important; }');
  });
  // a story as a unit: its own box and rules, set flat on the page (no tilt,
  // shadow or outside margin), copy in two columns when it is two wide
  var XE = P + ' [data-sai-shape="E"]';
  rcRules.push(XE + ' { display: block !important; margin: 0 !important; transform: none !important; width: auto !important; max-width: none !important; box-shadow: none !important; flex: none !important; }');
  rcRules.push(XE + ' > span:first-child { position: static !important; display: inline-block !important; margin: 0 0 6px !important; }');
  rcRules.push(XE + ' div { min-width: 0 !important; }');
  rcRules.push(XE + '[data-sai-ew="1"] > div { flex: 1 1 100% !important; max-width: 100% !important; border-left: 0 !important; }');
  rcRules.push(XE + '[data-sai-ew="2"] div:has(> p):not([data-m="sai-dispatch-cols"] *) { column-count: 2 !important; column-gap: 16px !important; column-rule: 1px solid rgba(20,20,18,.22) !important; }');
  rcRules.push(XE + '[data-sai-ew="1"] [data-m="sai-dispatch-cols"] { display: block !important; column-count: 1 !important; }');
  rcRules.push(XE + ' div:has(> p) > p { break-inside: avoid-column; }');
  // headlines sized off the window width (clamp(..vw..)) get a sheet size
  rcRules.push(XE + ' [style*="font-size:clamp("], ' + XE + ' [style*="Alfa Slab"] { font-size: 22px !important; line-height: 1.1 !important; }');
  rcRules.push(XE + '[data-sai-ew="1"] [style*="font-size:clamp("], ' + XE + '[data-sai-ew="1"] [style*="Alfa Slab"] { font-size: 17px !important; }');
  // the K-219/Vanunu sidebar and the ad grid beside it, dissolved into one grid
  rcRules.push(P + ' [data-sai-flat] { display: contents !important; }');
  var rcCss = document.createElement('style');
  rcCss.setAttribute('data-m', 'sai-ratecard-css');
  rcCss.textContent = '@media (min-width: 901px) {' + rcRules.join('') + '}';
  (document.head || document.documentElement).appendChild(rcCss);

  var rcDyn = null;          // per-page geometry (rows, depth, cell placement)
  var rcDynText = '';
  var rcCache = {};          // page key -> layout, so a revisit looks identical
  var rcNow = null;          // the page the engine currently owns

  function rcShown(el) { return getComputedStyle(el).display !== 'none'; }

  // The cards, the full-width blocks around them (a department header) and
  // the page's story (anything set in paragraphs), in page order. A K-219 or
  // Vanunu story sits in a sidebar beside the ad grid, both inside sai-body:
  // there the grid is laid on sai-body itself with the sidebar and the ad
  // grid dissolved into it (data-sai-flat), so the story box is a unit too.
  function rcCollect(page) {
    var grid = page.querySelector('[data-m="sai-grid"]');
    if (!grid || !rcShown(grid)) return null;
    var cont = grid.parentElement;
    if (!cont || !rcShown(cont)) return null;
    var cards = [];
    var pre = [];
    var post = [];
    var stories = [];
    var flat = [];
    var body = cont.parentElement;
    var side = body && body.getAttribute('data-m') === 'sai-body' && !body.hasAttribute('data-m2') ?
      body.querySelector(':scope > [data-m="sai-sidebar"]') : null;
    if (side && rcShown(side)) {
      Array.prototype.forEach.call(side.children, function (ch) {
        if (rcShown(ch) && ch.querySelector('p')) stories.push(ch);
      });
      if (stories.length) flat = [side, cont];
    }
    Array.prototype.forEach.call(cont.children, function (ch) {
      if (ch === grid) {
        Array.prototype.forEach.call(grid.children, function (g) {
          if (g.getAttribute('data-m') === 'sai-ad' && rcShown(g)) cards.push(g);
        });
      } else if (ch.getAttribute('data-m') === 'sai-ad') {
        if (rcShown(ch)) cards.push(ch);
      } else if (rcShown(ch) && getComputedStyle(ch).position !== 'absolute' && ch.getBoundingClientRect().height > 0) {
        if (ch.querySelector('p')) stories.push(ch); else (cards.length ? post : pre).push(ch);
      }
    });
    // more than one story (none today): the rest run full width at the head
    if (stories.length > 1) { pre = pre.concat(stories.slice(1)); stories = stories.slice(0, 1); }
    return { cont: flat.length ? body : cont, cards: cards, pre: pre, post: post, stories: stories, flat: flat };
  }

  function rcInfo(card) {
    var priceEl = card.querySelector('[data-m="sai-ad-price"]');
    if (!priceEl) return { el: card, brief: true, len: (card.textContent || '').replace(/\s+/g, ' ').length };
    var head = card.querySelector('[data-m="sai-ad-head"]');
    var title = ((head || card).textContent || '').trim();
    var price = parseFloat((priceEl.textContent || '').replace(/[^0-9.]/g, '')) || 0;
    return { el: card, brief: false, title: title, weight: Math.log(1 + price), o: WIDE_RE.test(title) ? 1 : TALL_RE.test(title) ? -1 : 0 };
  }

  // Modules of column depth a brief's copy needs. Measured live on the
  // issue's briefs: a one-column brief runs about 40 + 0.8 px per character
  // at the full 11.5px body size; copy-fitting may take it down to ~10.5px
  // (80% of that depth) before a deeper column is worth its space.
  function briefDepth(len, modH) {
    var est = (40 + 0.8 * len) * 0.8;
    return Math.max(2, Math.ceil((est + RC_GAP) / (modH + RC_GAP)));
  }

  function rcPageNo(c) {
    var m = /PAGE (\d+)/i.exec(c.label || '');
    return m ? parseInt(m[1], 10) : (parseInt(String(c.key).replace(/\D/g, ''), 10) || 0) + 1;
  }

  function permute(arr, cb) {
    if (arr.length > 6) { cb(arr); return; }
    (function rec(a, k) {
      if (k === a.length) { cb(a); return; }
      for (var i = k; i < a.length; i++) {
        var t = a[k]; a[k] = a[i]; a[i] = t;
        rec(a, k + 1);
        t = a[k]; a[k] = a[i]; a[i] = t;
      }
    })(arr.slice(), 0);
  }

  // The biggest units go to the biggest-ticket items; among units of the same
  // size, long goods take the wide one and garments/books the tall one.
  function rcAssign(pieces, items) {
    var slots = pieces.filter(function (p) { return p.u !== 'B' && p.u !== 'E'; }).sort(function (a, b) {
      return (b.w * b.h - a.w * a.h) || (a.r - b.r) || (a.c - b.c);
    });
    var order = items.slice().sort(function (a, b) { return b.weight - a.weight; });
    var out = [];
    for (var i = 0; i < slots.length;) {
      var area = slots[i].w * slots[i].h;
      var j = i;
      while (j < slots.length && slots[j].w * slots[j].h === area) j++;
      var g = slots.slice(i, j);
      var its = order.slice(i, j);
      var best = its;
      var bestS = -1e9;
      permute(its, function (perm) {
        var s = 0;
        perm.forEach(function (it, k) { s += it.o * Math.sign(g[k].w - g[k].h) - 0.01 * Math.abs(k - its.indexOf(it)); });
        if (s > bestS) { bestS = s; best = perm.slice(); }
      });
      best.forEach(function (it, k) { out.push({ item: it, piece: g[k] }); });
      i = j;
    }
    return out;
  }

  // Depth left for the grid: from its top to the foot margin, less whatever
  // follows it on the sheet; `avail` is what the module rows themselves get.
  function rcDepth(c, col, pr, f, blocks) {
    var cr = col.cont.getBoundingClientRect();
    var ir = c.inner.getBoundingClientRect();
    var top = (cr.top - pr.top) / f;
    var trailing = Math.max(0, (ir.bottom - cr.bottom) / f);
    var blocksH = 0;
    blocks.forEach(function (b) {
      var cs = getComputedStyle(b);
      blocksH += b.getBoundingClientRect().height / f + (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
    });
    var H = SHEET_H - RC_FOOT - trailing - top;
    var avail = H - blocksH - RC_GAP * blocks.length;
    return { H: H, avail: avail, R: Math.min(RC_MAX_ROWS, Math.floor((avail + RC_GAP) / (RC_MIN_MODULE + RC_GAP))) };
  }

  // The full-width blocks of a page under a given layout: the story joins
  // them only when it could not be set as a unit.
  function rcBlocks(col, mode) {
    return col.pre.concat(mode === 'block' ? col.stories.slice(0, 1) : [], col.post);
  }

  // Natural depth (sheet px) of a story set as a w-column unit at an 11px body.
  function rcStoryHeight(el, w, colW, f) {
    var keep = {};
    ['data-sai-shape', 'data-sai-ew', 'data-sai-bfs'].forEach(function (a) { keep[a] = el.getAttribute(a); });
    var style = el.getAttribute('style');
    el.setAttribute('data-sai-shape', 'E');
    el.setAttribute('data-sai-ew', String(w));
    el.setAttribute('data-sai-bfs', '11');
    el.style.setProperty('width', (w * colW + (w - 1) * RC_GAP) + 'px', 'important');
    var h = el.getBoundingClientRect().height / f;
    Object.keys(keep).forEach(function (a) { if (keep[a] == null) el.removeAttribute(a); else el.setAttribute(a, keep[a]); });
    if (style == null) el.removeAttribute('style'); else el.setAttribute('style', style);
    return h;
  }

  function rcPlan(c, col, infos, key, pr, f) {
    var items = infos.filter(function (x) { return !x.brief; });
    var briefs = infos.filter(function (x) { return x.brief; });
    var story = col.stories[0] || null;
    var dp = rcDepth(c, col, pr, f, rcBlocks(col, story ? 'unit' : null));
    var H = dp.H;
    var R = dp.R;
    if (R < 1 || items.length + briefs.length > RC_COLS * R) return null;
    if (!items.length && !story) {
      // a page of editorial only: its briefs stand side by side as columns
      // that run down to the same foot line as every other page
      return {
        id: 'rc' + Math.floor(hash(key) * 2e9).toString(36),
        H: H, R: 1, cols: briefs.length, nb: col.pre.length + col.post.length, footFix: 0,
        cells: briefs.map(function (x, k) { return { u: 'B', r: 0, c: k, w: 1, h: 1, hero: false }; }),
        fit: briefs.map(function () { return {}; })
      };
    }
    var modH = (dp.avail - RC_GAP * (R - 1)) / R;
    var need = 0;
    briefs.forEach(function (x) { need = Math.max(need, briefDepth(x.len, modH)); });
    var pageNo = rcPageNo(c);
    var pick = null;
    var mode = null;
    if (story) {
      // The story is an editorial unit at the head of the gutter, one or two
      // columns wide and as deep as its copy needs (copy-fitting may take
      // ~10% off); the ads are cut around it. Whichever width leaves the
      // better page, less any depth it would leave blank, wins.
      var colW = (col.cont.getBoundingClientRect().width / f - 2 * RC_GAP) / RC_COLS;
      [1, 2].forEach(function (w) {
        var nat = rcStoryHeight(story, w, colW, f);
        var h = Math.max(1, Math.ceil((nat * 0.9 + RC_GAP) / (modH + RC_GAP)));
        if (h > R || w * h + items.length + briefs.length > RC_COLS * R) return;
        var got = chooseCut(R, items.length, briefs.length, pageNo, need, { w: w, h: h });
        if (!got) return;
        got.score -= 8 * Math.max(0, h * modH + (h - 1) * RC_GAP - nat) / modH;
        if (!pick || got.score > pick.score) pick = got;
      });
      mode = 'unit';
      if (!pick) {
        // too long to stand beside the ads: run it across the head instead
        mode = 'block';
        dp = rcDepth(c, col, pr, f, rcBlocks(col, mode));
        H = dp.H;
        R = dp.R;
        if (R < 1 || items.length + briefs.length > RC_COLS * R) return null;
        modH = (dp.avail - RC_GAP * (R - 1)) / R;
      }
    }
    if (!pick) pick = chooseCut(R, items.length, briefs.length, pageNo, need, null);
    if (!pick) return null;
    var cut = pick.cut;
    // the cut is drawn for a right-hand page; a left-hand page is its mirror,
    // so the dominant unit stays on the outside edge and editorial on the gutter
    var verso = pageNo % 2 === 0;
    var pieces = cut.map(function (p) {
      return { u: p.u, r: p.r, w: p.w, h: p.h, c: verso ? RC_COLS - p.c - p.w : p.c };
    });
    var cells = new Array(infos.length);
    rcAssign(pieces, items).forEach(function (a) { cells[infos.indexOf(a.item)] = a.piece; });
    var bp = pieces.filter(function (p) { return p.u === 'B'; }).sort(function (a, b) { return (a.r - b.r) || (a.c - b.c); });
    briefs.forEach(function (x, k) { cells[infos.indexOf(x)] = bp[k]; });
    // the story's unit rides at the end, after the cards
    pieces.forEach(function (p) { if (p.u === 'E') cells.push(p); });
    var maxA = 0;
    var nMax = 0;
    pieces.forEach(function (p) {
      if (p.u === 'B' || p.u === 'E') return;
      var a = p.w * p.h;
      if (a > maxA) { maxA = a; nMax = 1; } else if (a === maxA) { nMax++; }
    });
    return {
      id: 'rc' + Math.floor(hash(key) * 2e9).toString(36),
      H: H, R: R, mode: mode, nb: rcBlocks(col, mode).length, footFix: 0,
      cells: cells.map(function (p) {
        return { u: p.u, r: p.r, c: p.c, w: p.w, h: p.h, hero: p.u !== 'B' && p.u !== 'E' && nMax === 1 && maxA >= 2 && p.w * p.h === maxA };
      }),
      fit: cells.map(function () { return {}; })
    };
  }

  // Editorial blocks (the department header, a story) stack at the head in
  // page order and the units fill the rest of the sheet down to the foot,
  // the way SOF ran a story above the ads stacked at the bottom of a page.
  function rcWriteCss(L) {
    var sel = P + ' [data-sai-rate="' + L.id + '"][data-sai-rate]';
    var nb = L.nb;
    var rows = [];
    var i;
    for (i = 0; i < nb; i++) rows.push('auto');
    rows.push('repeat(' + L.R + ', minmax(0, 1fr))');
    var t = '@media (min-width: 901px) {' + sel + ' { height: ' + (L.H - L.footFix).toFixed(1) +
      'px !important; grid-template-rows: ' + rows.join(' ') + ' !important;' +
      (L.cols ? ' grid-template-columns: repeat(' + L.cols + ', minmax(0, 1fr)) !important;' : '') + ' }';
    for (i = 0; i < nb; i++) {
      t += sel + ' [data-sai-blk="' + i + '"] { grid-row: ' + (i + 1) + ' !important; grid-column: 1 / -1 !important; }';
    }
    L.cells.forEach(function (p, j) {
      t += sel + ' [data-sai-cell="' + j + '"][data-sai-cell] { grid-area: ' + (nb + p.r + 1) + ' / ' + (p.c + 1) +
        ' / span ' + p.h + ' / span ' + p.w + ' !important; }';
    });
    t += '}';
    if (!rcDyn) {
      rcDyn = document.createElement('style');
      rcDyn.setAttribute('data-m', 'sai-ratecard-page');
      (document.head || document.documentElement).appendChild(rcDyn);
    }
    if (rcDynText !== t) { rcDyn.textContent = t; rcDynText = t; }
  }

  function rcSet(el, name, val) {
    if (val == null) { if (el.hasAttribute(name)) el.removeAttribute(name); } else if (el.getAttribute(name) !== val) { el.setAttribute(name, val); }
  }

  var RC_ATTRS = ['data-sai-cell', 'data-sai-shape', 'data-sai-ew', 'data-sai-hero', 'data-sai-clamp', 'data-sai-tclamp', 'data-sai-bfs'];

  // Put the engine's attributes (back) on the page; false if the runtime has
  // replaced any of its elements since.
  function rcMark() {
    var N = rcNow;
    if (!N) return true;
    if (!N.cont.isConnected) return false;
    rcSet(N.cont, 'data-sai-rate', N.L.id);
    for (var k = 0; k < N.flat.length; k++) {
      if (!N.flat[k].isConnected) return false;
      rcSet(N.flat[k], 'data-sai-flat', '1');
    }
    for (var i = 0; i < N.blocks.length; i++) {
      if (!N.blocks[i].isConnected) return false;
      rcSet(N.blocks[i], 'data-sai-blk', String(i));
    }
    for (var j = 0; j < N.cards.length; j++) {
      var el = N.cards[j];
      if (!el.isConnected) return false;
      var p = N.L.cells[j];
      var fz = N.L.fit[j];
      rcSet(el, 'data-sai-cell', String(j));
      rcSet(el, 'data-sai-shape', p.u);
      rcSet(el, 'data-sai-ew', p.u === 'E' ? String(p.w) : null);
      rcSet(el, 'data-sai-hero', p.hero ? '1' : null);
      rcSet(el, 'data-sai-size', null);
      rcSet(el, 'data-sai-clamp', fz.clamp != null ? String(fz.clamp) : null);
      rcSet(el, 'data-sai-tclamp', fz.tclamp != null ? String(fz.tclamp) : null);
      rcSet(el, 'data-sai-bfs', fz.bfs || null);
    }
    return true;
  }

  function rcClear() {
    var N = rcNow;
    rcNow = null;
    if (!N) return;
    N.cont.removeAttribute('data-sai-rate');
    N.flat.forEach(function (el) { el.removeAttribute('data-sai-flat'); });
    N.blocks.forEach(function (b) { b.removeAttribute('data-sai-blk'); });
    N.cards.forEach(function (el) { RC_ATTRS.forEach(function (a) { el.removeAttribute(a); }); });
  }

  function rcOwns(c) { return !!(rcNow && c && rcNow.page === c.el); }

  // How far a card's copy runs past its unit (sheet px; 0 = it fits).
  function rcOverflow(el, p, f) {
    var i;
    var bottom = -1e9;
    if (p.u === 'E') {
      // stories differ inside; their own box clips, so its scroll depth is the copy's
      var rh = el.getBoundingClientRect().height;
      var k = el.clientHeight ? rh / el.clientHeight : 1;
      return { over: Math.max(0, el.scrollHeight - el.clientHeight) * k / f, have: rh / f };
    }
    if (p.u === 'B') {
      var wrap = el.firstElementChild;
      if (!wrap) return { over: 0 };
      var wr = wrap.getBoundingClientRect();
      for (i = 0; i < wrap.children.length; i++) bottom = Math.max(bottom, wrap.children[i].getBoundingClientRect().bottom);
      var padB = parseFloat(getComputedStyle(wrap).paddingBottom) || 0;
      return { over: Math.max(0, (bottom - wr.bottom) / f + padB), have: wr.height / f };
    }
    var body = el.querySelector('[data-m="sai-ad-body"]');
    var text = body && body.lastElementChild;
    if (!body || !text) return { over: 0 };
    var blurb = el.querySelector('[data-m="sai-ad-blurb"]');
    var lineH = (SPEC[p.u].b || 12) * 1.4;
    var lines = blurb && rcShown(blurb) ? Math.round(blurb.getBoundingClientRect().height / f / lineH) : 0;
    var tr = text.getBoundingClientRect();
    if (SPEC[p.u].row) {
      for (i = 0; i < text.children.length; i++) {
        var r = text.children[i].getBoundingClientRect();
        if (r.height > 0) bottom = Math.max(bottom, r.bottom);
      }
      return { over: Math.max(0, (bottom - tr.bottom) / f), lines: lines };
    }
    var br = body.getBoundingClientRect();
    var photo = el.querySelector('[data-m="sai-ad-photo"]');
    var cardH = el.getBoundingClientRect().height / f;
    var ph = photo ? photo.getBoundingClientRect().height / f : 0;
    var minPh = Math.max(64, cardH * 0.4);
    var padBody = parseFloat(getComputedStyle(body).paddingBottom) || 0;
    return { over: Math.max(0, minPh - ph) + Math.max(0, (tr.bottom - br.bottom) / f + padBody), lines: lines };
  }

  // Copy-fit, as a period typesetter would: start every card at its unit's
  // full spec, then take copy lines (then a title line) out of whatever still
  // runs long; editorial columns step down a body size instead. All reads of
  // a pass happen before its writes, so each pass costs one layout.
  function rcFit(f) {
    var N = rcNow;
    var L = N.L;
    L.cells.forEach(function (p, j) {
      L.fit[j] = (p.u === 'B' || p.u === 'E') ? { bfs: BRIEF_SIZES[0] } : { clamp: SPEC[p.u].bl, tclamp: SPEC[p.u].tl };
    });
    rcMark();
    for (var pass = 0; pass < 6; pass++) {
      var reads = N.cards.map(function (el, j) { return rcOverflow(el, L.cells[j], f); });
      var changed = false;
      reads.forEach(function (d, j) {
        var p = L.cells[j];
        var fz = L.fit[j];
        if (!(d.over > 1)) return;
        if (p.u === 'B' || p.u === 'E') {
          var k = BRIEF_SIZES.indexOf(fz.bfs);
          if (k < BRIEF_SIZES.length - 1) {
            // body copy depth goes roughly with the square of its size
            var want = parseFloat(fz.bfs) * Math.sqrt(Math.max(0.3, (d.have - 90) / (d.have - 90 + d.over)));
            var next = k + 1;
            while (next < BRIEF_SIZES.length - 1 && parseFloat(BRIEF_SIZES[next]) > want) next++;
            fz.bfs = BRIEF_SIZES[next];
            changed = true;
          }
          return;
        }
        var lineH = (SPEC[p.u].b || 12) * 1.4;
        if (fz.clamp > 0 && d.lines > 0) {
          var nc = Math.max(0, Math.min(fz.clamp, d.lines) - Math.ceil(d.over / lineH));
          if (nc !== fz.clamp) { fz.clamp = nc; changed = true; return; }
        }
        if (fz.tclamp > 1) { fz.tclamp--; changed = true; }
      });
      if (!changed) break;
      rcMark();
    }
  }

  // Anything that still reaches past the foot margin comes out of the grid's depth.
  function rcCheckFoot(c, f) {
    var L = rcNow.L;
    var pr = c.el.getBoundingClientRect();
    var ir = c.inner.getBoundingClientRect();
    var over = (ir.bottom - pr.top) / f - (SHEET_H - RC_FOOT);
    if (over > 1 && L.footFix + over < L.H / 3) {
      L.footFix += Math.ceil(over);
      rcWriteCss(L);
    }
  }

  // Lay the current page out on the rate card. Returns false (and leaves the
  // page to the plain two-column layout) when it has no cards, more than two
  // briefs, or too little depth left for one module row.
  function rcLayout(c, refit) {
    if (isPhone() || !c || !c.inner) { rcClear(); return false; }
    var col = rcCollect(c.el);
    if (!col) { rcClear(); return false; }
    var infos = col.cards.map(rcInfo);
    var nItems = infos.filter(function (x) { return !x.brief; }).length;
    // a page that is one long feature and nothing else: section 4 fills it
    if (!nItems && col.stories.length) { rcClear(); return false; }
    if (!infos.length || infos.length - nItems > 2) { rcClear(); return false; }
    var key = c.key + '|' + col.pre.length + '/' + col.post.length + '/' + col.stories.length + '|' +
      infos.map(function (x) { return x.brief ? 'b' + x.len : x.title; }).join('|');
    if (rcNow && (rcNow.key !== key || rcNow.page !== c.el)) rcClear();
    c.inner.style.zoom = '';
    var pr = c.el.getBoundingClientRect();
    var f = pr.width / SHEET_W;
    if (!(f > 0)) return false;
    var L = rcCache[key];
    if (L && refit) {
      // fonts and photos settle after a turn and can move the grid's top
      // (a story above it reflows): take the new depth, and cut the page
      // again only if it now holds a different number of module rows
      var dp = rcDepth(c, col, pr, f, rcBlocks(col, L.mode));
      if (dp.R !== L.R && !L.cols) { L = null; } else { L.H = dp.H; }
    }
    if (!L) {
      L = rcPlan(c, col, infos, key, pr, f);
      if (!L) { delete rcCache[key]; rcClear(); return false; }
      rcCache[key] = L;
      refit = true;
    }
    rcNow = {
      key: key, page: c.el, L: L, cont: col.cont, flat: col.flat,
      blocks: rcBlocks(col, L.mode),
      cards: col.cards.concat(L.mode === 'unit' ? col.stories.slice(0, 1) : [])
    };
    rcWriteCss(L);
    rcMark();
    if (refit) {
      L.footFix = 0;
      rcWriteCss(L);
      rcFit(f);
      rcCheckFoot(c, f);
    }
    return true;
  }

  /* ---------- 4. Screen fit + magnifier ---------- */

  var EDGE_TOP = 26;      // room above the sheet for the page-number label
  var EDGE_BOTTOM = 10;
  var MIN_SCALE = 0.2;
  var REMEASURE_MS = [150, 500, 1200, 2500, 4000]; // images and fonts settle after a turn

  var lastKey = null;
  var lastW = 0;
  var lastH = 0;
  var scale = 1;
  var innerScale = 1;
  var target = null;
  var remeasureAt = [];
  var needScroll = false;
  var trim = 1;            // correction learned from the real bottom edge, kept across re-measures
  var verify = 0;          // frames left to check the real bottom edge after a resize
  var sheetPx = null;      // on-screen height of a settled interior page; covers match it
  var cur = null;

  var canHover = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  var magOn = false;      // off by default (merchant); the corner button turns it on
  try { magOn = localStorage.getItem('sai-magnifier') === 'on'; } catch (e) {}
  var hovering = false;
  var originX = 50;
  var originY = 50;
  var magEl = null;        // element currently carrying the magnifier transform
  var btn = null;
  var sndBtn = null;      // page-turn sound on/off (section 1 owns the state)
  var folio = null;

  function visible(el) {
    return !!el && el.offsetParent !== null && getComputedStyle(el).display !== 'none';
  }

  function isPhone() { return window.innerWidth <= 900; }

  // Phones (merchant: "make each page just take the phone screen 90% with
  // no scrolling needed"): while the issue is open the page is laid out at
  // desktop width and the browser shrinks it to the screen, so a phone shows
  // the real magazine sheet, fitted, and pinch-zoom reads it. Closing the
  // issue puts the site's own viewport back.
  var HANDHELD = !!(window.screen && Math.min(screen.width, screen.height) < 700 &&
    (('ontouchstart' in window) || navigator.maxTouchPoints > 0));
  var DESK_W = 980;
  var vpMeta = document.querySelector('meta[name="viewport"]');
  var vpOrig = vpMeta ? vpMeta.getAttribute('content') : '';
  function issueOpen() {
    var is = document.querySelector('#store [data-m="sai-issue"]');
    return !!(is && is.offsetParent);
  }
  if (HANDHELD && vpMeta) {
    // the page-turn arrows shrink with the page (merchant: "the buttons to
    // turn page are hard to hit"): on a phone they are drawn ~2.6x larger,
    // about a fingertip once the browser scales the page down
    var navCss = document.createElement('style');
    navCss.setAttribute('data-m', 'sai-handheld-css');
    navCss.textContent =
      'html[data-sai-handheld="1"] #store [data-m="sai-margin-nav"] { width: 120px !important; height: 120px !important; line-height: 116px !important; font-size: 56px !important; border-width: 3px !important; }' +
      'html[data-sai-handheld="1"] #store [data-m="sai-margin-nav"][style*="left:"] { left: 4px !important; }' +
      'html[data-sai-handheld="1"] #store [data-m="sai-margin-nav"][style*="right:"] { right: 4px !important; }';
    (document.head || document.documentElement).appendChild(navCss);
    setInterval(function () {
      var open = issueOpen();
      var want = open ? 'width=' + DESK_W + ', user-scalable=yes, maximum-scale=5' : vpOrig;
      if (vpMeta.getAttribute('content') !== want) vpMeta.setAttribute('content', want);
      var h = open ? '1' : '0';
      if (document.documentElement.getAttribute('data-sai-handheld') !== h) document.documentElement.setAttribute('data-sai-handheld', h);
    }, 300);
  }

  // Every page turn in assets/sat-import-pager-patch.js (and opening the
  // issue or its back cover in sat-app.js) ends in window.scrollTo(0, 0).
  // With the sheet fitted to the window that jumped to the top of the site,
  // and the loop below only scrolled back on the next frame: one frame of the
  // site header on every turn (merchant: "page still flashes full screen";
  // seen frame by frame in a screen recording). While the issue is open on
  // a desktop, that jump goes straight to where the sheet sits instead.
  var sheetScrollY = null;   // the scroll position that shows the current sheet
  var priorScrollTo = window.scrollTo;
  window.scrollTo = function (x, y) {
    var st = window.SATApp && window.SATApp.state;
    if (arguments.length >= 2 && +x === 0 && +y === 0 && sheetScrollY != null && !isPhone() &&
        st && st.page === 'store' && st.saiOpen) {
      return priorScrollTo.call(window, 0, sheetScrollY);
    }
    return priorScrollTo.apply(window, arguments);
  };

  function coverBox() {
    var img = document.querySelector('#store img[src*="sai-cover"]');
    return img && visible(img) ? img.parentElement : null;
  }

  function backBox() {
    var p = document.querySelector('#store sat-photo[slot-id="sai-backcover-photo"]');
    return p && visible(p) ? p.parentElement : null;
  }

  function interiorPage() {
    var pages = document.querySelectorAll('#store [data-m="sai-page"]');
    for (var i = 0; i < pages.length; i++) {
      if (visible(pages[i])) return { el: pages[i], index: i };
    }
    return null;
  }

  // Height an element takes in the flow; absolutely placed overlays take none.
  function outerH(el) {
    if (!el || !visible(el)) return 0;
    var cs = getComputedStyle(el);
    if (cs.position === 'absolute' || cs.position === 'fixed') return 0;
    return el.getBoundingClientRect().height + parseFloat(cs.marginTop) + parseFloat(cs.marginBottom);
  }

  // What is on screen: the sheet to scale, the space its own controls need,
  // the element whose top should sit at the top of the window, the last
  // control below it, and the element the magnifier enlarges.
  function current() {
    var c = coverBox();
    if (c) {
      return { key: 'cover', el: c, reserve: outerH(c.nextElementSibling), anchor: c, last: c, kind: 'cover',
        mag: c.querySelector('img'), label: 'Front cover' };
    }
    var b = backBox();
    if (b) {
      return { key: 'back', el: b, reserve: outerH(b.nextElementSibling), anchor: b, last: b, kind: 'cover',
        mag: b.querySelector('sat-photo'), label: 'Back cover' };
    }
    var p = interiorPage();
    if (p) {
      var issue = document.querySelector('#store [data-m="sai-issue"]');
      var topNav = issue && issue.firstElementChild !== p.el ? issue.firstElementChild : null;
      var print = issue && issue.querySelector('[data-m="print-btn"]');
      var bottomNav = print ? print.parentElement : null;
      var inner = p.el.querySelector('[data-m="sai-inner"]');
      return {
        key: 'page' + p.index,
        el: p.el,
        inner: inner,
        reserve: outerH(topNav) + outerH(bottomNav),
        anchor: visible(topNav) ? topNav : p.el,
        last: visible(bottomNav) ? bottomNav : p.el,
        kind: inner ? 'catalog' : 'ad',
        mag: inner || p.el.querySelector('img'),
        label: folioText(issue)
      };
    }
    return null;
  }

  function folioText(issue) {
    if (!issue) return '';
    var spans = issue.querySelectorAll('span');
    for (var i = 0; i < spans.length; i++) {
      var t = (spans[i].textContent || '').trim();
      if (/^PAGE \d+ OF \d+$/i.test(t)) return t;
    }
    return '';
  }

  function canScale(c) {
    // mobile css pins interior pages (ads included) at zoom 1 on purpose
    return !(isPhone() && c.kind !== 'cover');
  }

  // Box size per card, decided per page: the priciest item gets the featured
  // box, the next few standard (one tall), the cheapest the small ones. Pages
  // that carry a story or wire brief use only standard and small boxes so the
  // story keeps the emphasis. Measured live: every page then lands at 76-102%
  // of its sheet. Reasserted every frame (the runtime rewrites style attrs).
  var SIZE_ORDER = { F: 1, S: 2, T: 3, C: 4 };
  function cardPrice(card) {
    var el = card.querySelector('[data-m="sai-ad-price"]');
    return el ? (parseFloat((el.textContent || '').replace(/[^0-9.]/g, '')) || 0) : -1;
  }
  function assignSizes(page) {
    var cards = Array.prototype.slice.call(page.querySelectorAll('[data-m="sai-ad"]'));
    var items = [];
    var stories = [];
    cards.forEach(function (c) { (cardPrice(c) >= 0 ? items : stories).push(c); });
    items.sort(function (a, b) { return cardPrice(b) - cardPrice(a); });
    var hasStory = stories.length > 0 || !!page.querySelector('[data-m="sai-dispatch-cols"]') ||
      (!!page.querySelector('[data-m="sai-sidebar"]') && !page.querySelector('[data-m2="sai-cols"]'));
    var n = items.length;
    var plan;
    if (hasStory) plan = n >= 4 ? ['S', 'S', 'C', 'C', 'C', 'C'] : n === 3 ? ['S', 'C', 'C'] : ['S', 'S'];
    else if (n >= 6) plan = ['F', 'S', 'S', 'T', 'C', 'C'];
    else if (n === 5) plan = ['F', 'S', 'S', 'S', 'S'];
    else if (n === 4) plan = ['F', 'S', 'S', 'F'];
    else if (n === 3) plan = ['F', 'S', 'S'];
    else if (n === 2) plan = ['S', 'S'];
    else plan = ['F'];
    items.forEach(function (c, i) { setSize(c, plan[i] || 'C', String(SIZE_ORDER[plan[i] || 'C'] * 10 + i)); });
    stories.forEach(function (c) { setSize(c, 'S', '25'); });
  }
  function setSize(card, size, order) {
    if (card.getAttribute('data-sai-size') !== size) card.setAttribute('data-sai-size', size);
    if (card.style.order !== order) card.style.order = order;
  }

  // Content that runs past the bottom of its cover-shaped sheet is shrunk to
  // fit inside it; the sheet keeps its shape.
  function measureInner(c) {
    if (!c.inner || isPhone()) return 1;
    if (!rcOwns(c)) assignSizes(c.el);
    c.inner.style.zoom = '';
    var sheet = c.el.getBoundingClientRect().height;
    var content = c.inner.getBoundingClientRect().height;
    return content > sheet + 1 ? Math.max(0.5, sheet / content) : 1;
  }

  function availHeight(c) {
    var free = window.innerHeight - (isPhone() ? 24 : EDGE_TOP + EDGE_BOTTOM);
    // Covers match the interior pages exactly (same on-screen sheet), so
    // flipping from cover to page never jumps size.
    if (c.kind === 'cover' && sheetPx) return sheetPx;
    return free - c.reserve;
  }

  function measure(c) {
    var el = c.el;
    // '1', not '': clearing it would let the stylesheet zoom (section 4's
    // flash guard) stand, the sheet would measure already shrunk, and every
    // other fit came out 1 — the sheet flipped full size and back
    el.style.zoom = '1';
    var natural = el.getBoundingClientRect();
    var availW = el.parentElement ? el.parentElement.clientWidth : window.innerWidth;
    // on a phone in desktop layout the sheet may grow to ~88% of the width
    var cap = HANDHELD && !isPhone() ? 1.35 : 1;
    var s = Math.min(cap, availHeight(c) / natural.height, (availW * (cap > 1 ? 0.9 : 1)) / Math.max(1, natural.width));
    return Math.max(MIN_SCALE, s);
  }

  function zoomStr(z) { return z < 0.999 || z > 1.001 ? String(Math.round(z * 1000) / 1000) : ''; }
  var zoomCss = document.createElement('style');
  zoomCss.setAttribute('data-m', 'sai-zoom-css');
  (document.head || document.documentElement).appendChild(zoomCss);

  /* magnifier */

  function magActive() { return canHover && magOn && !isPhone(); }

  function magFactor(c) {
    // enough to bring the page back to roughly its natural, readable size
    var shown = (c.kind === 'cover' || c.kind === 'ad') ? scale : scale * innerScale;
    return Math.max(1.8, Math.min(3, 1 / Math.max(0.2, shown)));
  }

  document.addEventListener('mousemove', function (e) {
    if (!cur || !magActive()) { hovering = false; return; }
    var r = cur.el.getBoundingClientRect();
    var inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    hovering = inside;
    if (inside) {
      originX = Math.max(0, Math.min(100, (e.clientX - r.left) / r.width * 100));
      originY = Math.max(0, Math.min(100, (e.clientY - r.top) / r.height * 100));
    }
  }, { passive: true });
  document.addEventListener('mouseleave', function () { hovering = false; });

  function applyMagnifier(c) {
    var el = c && c.mag;
    if (magEl && magEl !== el) { magEl.style.transform = ''; magEl.style.transformOrigin = ''; }
    magEl = el || null;
    if (!el) return;
    var on = hovering && magActive();
    var t = on ? 'scale(' + magFactor(c).toFixed(2) + ')' : '';
    var o = on ? originX.toFixed(1) + '% ' + originY.toFixed(1) + '%' : '';
    if (el.style.transform !== t) el.style.transform = t;
    if (el.style.transformOrigin !== o) el.style.transformOrigin = o;
    if (el.style.transition !== 'transform .15s ease-out') el.style.transition = 'transform .15s ease-out';
  }

  /* controls: magnifier toggle + page label */

  function ensureControls() {
    if (btn) return;
    btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('data-m', 'sai-mag-toggle');
    btn.style.cssText = 'position:fixed;right:18px;bottom:18px;z-index:30;display:none;cursor:pointer;' +
      'font-family:Oswald,sans-serif;font-weight:600;font-size:12px;letter-spacing:.13em;text-transform:uppercase;' +
      'color:#F2EFE3;background:#B2201C;border:0;padding:11px 16px;box-shadow:0 3px 12px rgba(0,0,0,.45);';
    btn.addEventListener('click', function () {
      magOn = !magOn;
      try { localStorage.setItem('sai-magnifier', magOn ? 'on' : 'off'); } catch (e) {}
    });
    document.body.appendChild(btn);

    // page-turn sound on/off, above the magnifier (section 1 handles the
    // click and keeps the label: both run off data-m="sai-sound-btn")
    sndBtn = document.createElement('button');
    sndBtn.type = 'button';
    sndBtn.setAttribute('data-m', 'sai-sound-btn');
    sndBtn.setAttribute('aria-label', 'Page-turn sound on or off');
    sndBtn.style.cssText = 'position:fixed;right:18px;bottom:64px;z-index:30;display:none;cursor:pointer;' +
      'font-family:Oswald,sans-serif;font-weight:600;font-size:12px;letter-spacing:.13em;text-transform:uppercase;' +
      'color:#EAE1CB;background:rgba(20,18,14,.82);border:1px solid rgba(234,225,203,.45);padding:10px 15px;box-shadow:0 3px 12px rgba(0,0,0,.45);';
    sndBtn.innerHTML = '<span data-m="sai-sound-on">&#128264; Sound: on</span><span data-m="sai-sound-off">&#128263; Sound: off</span>';
    document.body.appendChild(sndBtn);

    folio = document.createElement('div');
    folio.setAttribute('data-m', 'sai-folio-label');
    folio.style.cssText = 'position:fixed;left:0;right:0;top:6px;z-index:20;display:none;text-align:center;pointer-events:none;' +
      'font-family:"Special Elite",monospace;font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:#D9C9A3;' +
      'text-shadow:0 1px 3px rgba(0,0,0,.9);';
    document.body.appendChild(folio);
  }

  function updateControls(c) {
    ensureControls();
    var desk = !!c && !isPhone();
    btn.style.display = desk && canHover ? 'block' : 'none';
    sndBtn.style.display = desk ? 'block' : 'none';
    sndBtn.style.bottom = desk && canHover ? '64px' : '18px';
    var label = magOn ? '🔍 Magnifier: on' : '🔍 Magnifier: off';
    if (btn.textContent !== label) btn.textContent = label;
    folio.style.display = desk ? 'block' : 'none';
    var text = c ? (c.label || '') + (desk && magActive() ? '  ·  hover to magnify' : '') : '';
    if (folio.textContent !== text) folio.textContent = text;
  }

  /* arrow keys turn pages (the side arrows are what actually turn them) */

  document.addEventListener('keydown', function (e) {
    if (!cur || e.altKey || e.ctrlKey || e.metaKey) return;
    var tag = (e.target && e.target.tagName) || '';
    if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag) || (e.target && e.target.isContentEditable)) return;
    var want = e.key === 'ArrowRight' ? '❯' : e.key === 'ArrowLeft' ? '❮' : null;
    if (!want) return;
    var spans = document.querySelectorAll('#store span');
    for (var i = 0; i < spans.length; i++) {
      var sp = spans[i];
      var spcs = getComputedStyle(sp);
      // fixed elements never have an offsetParent, so check display/rects instead of visible()
      if ((sp.textContent || '').trim() === want && spcs.position === 'fixed' && spcs.display !== 'none' && sp.getClientRects().length) {
        sp.click();
        e.preventDefault();
        return;
      }
    }
    if (want === '❯' && cur.kind === 'cover' && cur.key === 'cover') {
      var open = cur.el.querySelector('[onclick], div[style*="cursor:pointer"]');
      if (open) { open.click(); e.preventDefault(); }
    }
  });

  /* A page that is one long feature and nothing else (Hasenfus, Mammoth):
   * its body copy is set up to 30% larger so the story runs to the same foot
   * line as every other page instead of stopping short. Size, not zoom: the
   * story keeps its own width, so none of its parts re-wrap. */
  var fillEl = null;
  var fillDrop = [];      // the running header on a feature page, hidden
  var fillCss = null;
  var fillText = '';
  function writeFill(fs) {
    var t = '@media (min-width: 901px) {' + P + ' [data-sai-fill="1"] p { font-size: ' + fs.toFixed(2) +
      'px !important; line-height: 1.55 !important; }}';
    if (t !== fillText) { fillCss.textContent = t; fillText = t; }
  }
  function featureFill(c, measureNow) {
    if (!measureNow) {
      if (fillEl && fillEl.isConnected && fillEl.getAttribute('data-sai-fill') !== '1') fillEl.setAttribute('data-sai-fill', '1');
      fillDrop.forEach(function (b) { if (b.isConnected && !b.hasAttribute('data-sai-drop')) b.setAttribute('data-sai-drop', '1'); });
      return;
    }
    // show the header again first, so the page is collected as it really is
    fillDrop.forEach(function (b) { b.removeAttribute('data-sai-drop'); });
    fillDrop = [];
    var el = null;
    if (c && c.inner && !isPhone() && !rcOwns(c)) {
      var col = rcCollect(c.el);
      if (col && !col.cards.length && col.stories.length === 1 && !col.flat.length) {
        el = col.stories[0];
        fillDrop = col.pre.slice();
        fillDrop.forEach(function (b) { b.setAttribute('data-sai-drop', '1'); });
      }
    }
    if (fillEl && fillEl !== el) fillEl.removeAttribute('data-sai-fill');
    fillEl = el;
    if (!el) return;
    if (!fillCss) {
      fillCss = document.createElement('style');
      fillCss.setAttribute('data-m', 'sai-feature-fill');
      (document.head || document.documentElement).appendChild(fillCss);
    }
    el.removeAttribute('data-sai-fill');
    c.inner.style.zoom = '';
    var pr = c.el.getBoundingClientRect();
    var f = pr.width / SHEET_W;
    var room = (SHEET_H - RC_FOOT) - (c.inner.getBoundingClientRect().bottom - pr.top) / f;
    var h = el.getBoundingClientRect().height / f;
    var p0 = el.querySelector('p');
    if (room < 8 || h < 200 || !p0) { fillEl = null; return; }
    // the body size the story is set in (on the paragraph or the block holding it)
    var base = 11;
    for (var n = p0; n && n !== el.parentElement; n = n.parentElement) {
      var m = /font-size:\s*([\d.]+)px/.exec(n.getAttribute('style') || '');
      if (m) { base = parseFloat(m[1]); break; }
    }
    var target = h + room;
    var lo = base;
    var hi = base * 1.3;
    el.setAttribute('data-sai-fill', '1');
    for (var i = 0; i < 7; i++) {
      var fs = (lo + hi) / 2;
      writeFill(fs);
      if (el.getBoundingClientRect().height / f <= target) lo = fs; else hi = fs;
    }
    if (lo < base + 0.1) { el.removeAttribute('data-sai-fill'); fillEl = null; return; }
    writeFill(lo);
  }

  /* ---------- 6. Page 1: the contents ----------
   *
   * Merchant: "front page (after cover ofc) needs to be far better
   * designed", then "front page is a bit shitty on the import". Page 1 is
   * set like the contents page of Soldier of Fortune, November 1987
   * (archive.org soldier-of-fortune-1987-11-ocr, page n3): the masthead
   * over a dateline rule; the features in two ruled columns, each a kicker,
   * a heavy head and a one-line deck ending in its page number (read from
   * the pager's own issue index, so it can never drift from the real page
   * order; a listing turns to its page); the cover down the right with its
   * caption; the department filter and the search box under it as the
   * departments column; the ordering terms along the foot where SOF ran its
   * legal notice. Page 1 sells nothing (section 1 sends its two items to the
   * Supply Locker). The home page shows this same page as its Import
   * teaser, with the runtime's "Open The Import" box under it. Desktop only;
   * phones keep the stacked page.
   *
   * The runtime drops children it did not render whenever it renders
   * (several times a second, even idle), so a MutationObserver puts the
   * added blocks back as soon as they go, before the browser paints. */
  var TOC_COPY = {
    tomcat: ['Cover Story', 'The Super Tomcat', 'The F-14A Plus trades the TF30 for GE’s F110'],
    blade: ['Department', 'The Blade Room', 'Kukris, Bowies and tomahawks, hand-forged and on the shelf'],
    k219: ['International Defense', 'Star Wars Goes International', 'Israel signs a $5.1 million SDI research agreement'],
    hasenfus: ['Special Report', 'Washington’s Secret Ledger', 'The Iran arms initiative and the diverted funds'],
    vanunu: ['Defense Procurement', 'The Future of Air Defense', 'A modified F-16A wins the continental job'],
    mammoth: ['Wire Report', 'Mammoth Lakes Inquiry Goes Behind Closed Doors', 'Committee subpoenas records; 4,012 deaths unexplained'],
    reykjavik: ['Feature', 'The Next Generation of Air Superiority', 'Lockheed and Northrop face off for the ATF'],
    taxreform: ['Technology & Industry', 'The 386 Revolution', 'Intel’s 32-bit chip points to a new class of computing'],
    afghan: ['International Affairs', 'Afghanistan: The War Without an Exit', 'Moscow’s withdrawal meets a war that goes on'],
    gulf: ['International Affairs', 'The Gulf Burns', 'Iraq strikes Larak as the tanker war widens']
  };
  var ISSUE_DATE = 'November 1986';   // the cover's date
  var ISSUE_VOL = 'Vol. 4 · No. 11';
  var COVER_LINE = 'The Super Tomcat. Grumman’s F-14A Plus trades the troublesome TF30 for General Electric’s F110, and the Navy may finally get the Tomcat it wanted.';

  var T = '#store [data-m="sai-toc"]';
  var DL = '#store [data-m="sai-dateline"]';
  var CV = '#store [data-m="sai-cover-box"]';
  var M1 = P1 + ' [data-m="sai-inner"] > div:first-child';
  var S1 = P1 + ' [data-m2="sai-sidebar-p1"]';
  var BOX = S1 + ' > :is(:nth-child(1), :nth-child(2), :nth-child(3))';
  var DEP = S1 + ' > :nth-child(2) > div:nth-child(2) > div';
  var OI = S1 + ' > :nth-child(3) > div:nth-child(2)';
  var tocCss = document.createElement('style');
  tocCss.setAttribute('data-m', 'sai-contents-css');
  tocCss.textContent = T + ', ' + DL + ', ' + CV + ' { display: none; }' +
    '@media (min-width: 901px) {' +
    // the page runs to the same foot line as every other page; the
    // listing's rows take up whatever the columns beside it leave
    P1 + ' [data-m="sai-inner"] { height: 938px; box-sizing: border-box; display: flex !important; flex-direction: column; overflow: hidden; }' +
    P1 + ' [data-m="sai-body"] { display: flex !important; flex-direction: column; flex: 1 1 auto; min-height: 0; }' +
    P1 + ' [data-m="sai-body"] > [data-m="sai-adgrid"]:not(:has([data-m="sai-ad"])) { display: none !important; }' +
    P1 + ' [data-m="sai-body"] > div:nth-child(3) { flex: none; margin-top: 12px !important; }' +
    // on the home page that box ("Send For The Complete Book", shown only
    // there) takes the foot, so the ordering terms give way to it
    P1 + ':has([data-m="sai-body"] > div:nth-child(3)[style*="display:flex"]) [data-m2="sai-sidebar-p1"] > :nth-child(3) { display: none !important; }' +
    P1 + ' [data-m="sai-tab"] > span { font-size: 0 !important; }' +
    P1 + ' [data-m="sai-tab"] > span::after { content: "Contents"; font-size: 12px; text-transform: uppercase; }' +
    // masthead: the nameplate centred under a heavy rule, no coupon box
    // (there is no coupon on this page); its title is sized off the
    // window (clamp(..vw..)), so it gets a fixed sheet size here
    M1 + ' { flex: none; border: 0 !important; border-top: 6px solid #141412 !important; margin: 0 !important; justify-content: center; }' +
    M1 + ' > div:first-child { flex: 1 1 auto !important; padding: 8px 0 7px !important; align-items: center; text-align: center; }' +
    M1 + ' > div:first-child > div:first-child { font-size: 46px !important; letter-spacing: .015em !important; }' +
    M1 + ' > div:first-child > div:nth-child(2) { font-size: 10.5px !important; letter-spacing: .32em !important; margin-top: 6px !important; }' +
    M1 + ' > div:nth-child(2) { display: none !important; }' +
    S1 + ' { display: grid !important; flex: 1 1 auto; min-height: 0; margin: 0 !important; column-count: auto !important;' +
      ' grid-template-columns: repeat(5, minmax(0, 1fr)) !important; grid-template-rows: auto minmax(0, 1fr) auto auto auto;' +
      ' grid-template-areas: "dl dl dl dl dl" "toc toc toc cov cov" "toc toc toc dep dep" "toc toc toc fif fif" "oi oi oi oi oi";' +
      ' column-gap: 18px !important; row-gap: 12px !important; }' +
    S1 + ' > * { margin: 0 !important; min-width: 0; }' +
    S1 + ' > :nth-child(1) { grid-area: fif; }' +
    S1 + ' > :nth-child(2) { grid-area: dep; }' +
    S1 + ' > :nth-child(3) { grid-area: oi; }' +
    S1 + ' > :nth-child(7) { display: none !important; }' +
    S1 + ' > [data-m="sai-dateline"] { grid-area: dl; }' +
    S1 + ' > [data-m="sai-toc"] { grid-area: toc; }' +
    S1 + ' > [data-m="sai-cover-box"] { grid-area: cov; }' +
    // the runtime's boxes lose their frames and black bars: each is headed
    // by a rule and a bold label, as SOF heads its Departments list
    BOX + ' { border: 0 !important; background: none !important; }' +
    BOX + ' > div:first-child { background: none !important; padding: 3px 0 3px !important; border-top: 3px solid #141412; border-bottom: 1px solid #141412; margin-bottom: 6px; }' +
    BOX + ' > div:first-child > span { color: #141412 !important; font-family: Anton, sans-serif !important; font-weight: 400 !important; font-size: 14px !important; letter-spacing: .08em !important; }' +
    BOX + ' > div:nth-child(2) { padding: 0 !important; }' +
    // departments: a two-column list, the one showing marked in red
    DEP + ' { display: grid !important; grid-template-columns: 1fr 1fr; column-gap: 14px; }' +
    DEP + ' > span { background: none !important; color: #141412 !important; padding: 3px 0 !important; gap: 5px !important; border-bottom: 1px dotted rgba(20,20,18,.4); }' +
    DEP + ' > span > span:first-child { display: none !important; }' +
    DEP + ' > span > span:last-child { font-size: 11.5px !important; font-weight: 600 !important; letter-spacing: .05em !important; }' +
    DEP + ' > span[style*="background:#1A1A18"] { color: #8E2116 !important; }' +
    DEP + ' > span[style*="background:#1A1A18"]::before { content: "\\25B8"; font-size: 10px; line-height: 1; }' +
    DEP + ' > span:hover { color: #8E2116 !important; }' +
    // find it fast: the search box and the sort beside each other
    S1 + ' > :nth-child(1) > div:nth-child(2) { display: flex !important; gap: 6px; }' +
    S1 + ' > :nth-child(1) > div:nth-child(2) > input { flex: 1 1 58%; min-width: 0; margin: 0 !important; }' +
    S1 + ' > :nth-child(1) > div:nth-child(2) > select { flex: 1 1 42%; min-width: 0; }' +
    // ordering terms: the notice at the foot, the terms in two columns
    // and the mailing address ruled off beside them
    OI + ' { display: grid !important; grid-template-columns: minmax(0, 2.2fr) minmax(0, 1fr); column-gap: 18px; align-items: start; }' +
    OI + ' > div:first-child { display: block !important; column-count: 2; column-gap: 16px; }' +
    OI + ' > div:first-child > div { break-inside: avoid; margin-bottom: 4px; }' +
    OI + ' > div:nth-child(2) { border-top: 0 !important; margin-top: 0 !important; padding: 0 0 0 14px !important; border-left: 1px solid rgba(20,20,18,.45); }' +
    // dateline: issue date, CONTENTS, volume, between two rules
    DL + ' { display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #141412; border-bottom: 3px solid #141412; padding: 5px 0 4px; }' +
    DL + ' span { font-family: "Special Elite", monospace; font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: #141412; }' +
    DL + ' b { font-family: Anton, sans-serif; font-weight: 400; font-size: 15px; letter-spacing: .42em; text-transform: uppercase; color: #8E2116; padding-left: .42em; }' +
    // the listing
    T + ' { display: flex; flex-direction: column; min-height: 0; }' +
    T + ' .toc-list { flex: 1; min-height: 0; position: relative; display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: repeat(6, minmax(min-content, 1fr)); grid-auto-flow: column; column-gap: 18px; }' +
    T + ' .toc-list::before { content: ""; position: absolute; top: 0; bottom: 0; left: 50%; border-left: 1px solid rgba(20,20,18,.45); }' +
    T + ' .toc-e { border-top: 1px solid #141412; padding: 8px 0 6px; min-height: 0; overflow: hidden; cursor: pointer; }' +
    T + ' .toc-e:nth-child(5n+1) { border-top: 0; padding-top: 0; }' +
    T + ' .toc-k { font-family: "Special Elite", monospace; font-size: 9px; letter-spacing: .16em; text-transform: uppercase; color: #8E2116; }' +
    T + ' .toc-t { font-family: Anton, sans-serif; font-size: 18px; line-height: 1.02; letter-spacing: .005em; text-transform: uppercase; color: #141412; margin: 3px 0 5px; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; }' +
    T + ' .toc-d { font-family: Lora, serif; font-style: italic; font-size: 11.5px; line-height: 1.38; color: #2c2721; }' +
    T + ' .toc-n { font-family: Oswald, sans-serif; font-style: normal; font-weight: 700; font-size: 14px; color: #8E2116; margin-left: 4px; white-space: nowrap; }' +
    T + ' .toc-e:hover .toc-t, ' + T + ' .toc-e:focus .toc-t { color: #8E2116; }' +
    T + ' .toc-e:focus { outline: 1px dashed #8E2116; outline-offset: 2px; }' +
    T + ' .toc-ft { flex: none; margin-top: 6px; padding-top: 4px; border-top: 1px solid #141412; font-family: "Courier Prime", monospace; font-size: 8.5px; letter-spacing: .06em; text-transform: uppercase; color: #5a5148; }' +
    // the cover, down the right
    CV + ' { display: flex; flex-direction: column; min-height: 0; }' +
    CV + ' img { flex: 1 1 auto; min-height: 0; width: 100%; object-fit: cover; object-position: 50% 30%; display: block; border: 1px solid #141412; box-shadow: 2px 3px 0 rgba(20,20,18,.25); }' +
    CV + ' .cv-pg { font-family: "Special Elite", monospace; font-size: 8.5px; letter-spacing: .14em; text-transform: uppercase; color: #5a5148; margin-top: 5px; }' +
    CV + ' .cv-cap { font-family: Oswald, sans-serif; font-size: 10px; line-height: 1.4; color: #141412; margin-top: 3px; }' +
    CV + ' .cv-cap b { font-weight: 700; letter-spacing: .06em; color: #8E2116; }' +
    CV + ' .cv-cap .toc-n { font-family: Oswald, sans-serif; font-weight: 700; font-size: 13px; color: #8E2116; }' +
    CV + '[role="link"] { cursor: pointer; }' +
    '}';
  (document.head || document.documentElement).appendChild(tocCss);

  var dateline = null;
  var toc = null;
  var coverEl = null;
  var tocKey = '';
  var tocHost = null;
  var tocObs = null;
  var tocAdded = [];

  function tocEntries() {
    var list = [];
    Object.keys(storyPage).forEach(function (k) { if (TOC_COPY[k]) list.push({ page: storyPage[k], copy: TOC_COPY[k] }); });
    if (lockerPages.length) {
      list.push({ page: lockerPages[0], copy: ['Department', 'Supply Locker',
        'Surplus, field gear, comms and aviation, pp. ' + lockerPages[0] + '–' + lockerPages[lockerPages.length - 1]] });
    }
    return list.sort(function (a, b) { return a.page - b.page; });
  }

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  function turnTo(page, node) {
    function go() { if (window.SATApp && typeof window.SATApp.setState === 'function') window.SATApp.setState({ saiInteriorPage: page }); }
    node.setAttribute('role', 'link');
    node.tabIndex = 0;
    node.addEventListener('click', go);
    node.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } });
  }

  function buildToc(list) {
    while (toc.firstChild) toc.removeChild(toc.firstChild);
    var rows = el('div', 'toc-list');
    list.forEach(function (it) {
      var row = el('div', 'toc-e');
      row.appendChild(el('div', 'toc-k', it.copy[0]));
      row.appendChild(el('div', 'toc-t', it.copy[1]));
      var deck = el('div', 'toc-d', it.copy[2]);
      deck.appendChild(el('span', 'toc-n', String(it.page)));
      row.appendChild(deck);
      turnTo(it.page, row);
      rows.appendChild(row);
    });
    toc.appendChild(rows);
    toc.appendChild(el('div', 'toc-ft', 'Click a listing to turn to its page · arrow keys turn pages'));
  }

  function buildCover(src) {
    while (coverEl.firstChild) coverEl.removeChild(coverEl.firstChild);
    var img = el('img');
    img.src = src;
    img.alt = 'Front cover: the F-14A Plus';
    coverEl.appendChild(img);
    coverEl.appendChild(el('div', 'cv-pg', 'Front cover'));
    var cap = el('div', 'cv-cap');
    cap.appendChild(el('b', null, 'COVER STORY: '));
    cap.appendChild(document.createTextNode(COVER_LINE + ' '));
    if (storyPage.tomcat) {
      cap.appendChild(el('span', 'toc-n', String(storyPage.tomcat)));
      turnTo(storyPage.tomcat, coverEl);
    }
    coverEl.appendChild(cap);
  }

  function ensureToc(c) {
    var host = c && c.inner && !isPhone() ? c.el.querySelector('[data-m2="sai-sidebar-p1"]') : null;
    if (!host) return;
    var list = tocEntries();
    if (!list.length) return;
    if (!toc) {
      dateline = el('div');
      dateline.setAttribute('data-m', 'sai-dateline');
      dateline.appendChild(el('span', null, ISSUE_DATE));
      dateline.appendChild(el('b', null, 'Contents'));
      dateline.appendChild(el('span', null, ISSUE_VOL));
      toc = el('div');
      toc.setAttribute('data-m', 'sai-toc');
      coverEl = el('div');
      coverEl.setAttribute('data-m', 'sai-cover-box');
    }
    var art = document.querySelector('#store img[src*="sai-cover"]');
    var src = art ? art.src : (coverEl.querySelector('img') || {}).src || '';
    var key = list.map(function (it) { return it.page + ':' + it.copy[1]; }).join('|') + '|' + src + '|' + (storyPage.tomcat || '');
    if (key !== tocKey) {
      tocKey = key;
      buildToc(list);
      if (src) buildCover(src);
    }
    tocAdded = src ? [dateline, toc, coverEl] : [dateline, toc];
    if (tocHost !== host) {
      if (tocObs) tocObs.disconnect();
      tocHost = host;
      tocObs = new MutationObserver(function () { if (tocHost && tocHost.isConnected) putAdded(); });
      tocObs.observe(host, { childList: true });
    }
    putAdded();
  }

  /* Home page: the Import teaser shows the cover art, not the contents page
   * (merchant: "on the main page have the cover art be shown not page 1").
   * The home page draws this same Page 1 with its "Send For The Complete
   * Book" box, which only shows there; the cover fills the sheet, that box
   * sits along its foot, and the cover itself opens the Import. Phones too. */
  var HOME = P1 + ':has([data-m="sai-body"] > div:nth-child(3)[style*="display:flex"])';
  var homeCss = document.createElement('style');
  homeCss.setAttribute('data-m', 'sai-home-cover-css');
  homeCss.textContent = '[data-m="sai-home-cover"] { display: none; }' +
    HOME + ' { position: relative; aspect-ratio: 1024 / 1536; overflow: hidden; }' +
    HOME + ' [data-m="sai-inner"] { position: static !important; }' +
    HOME + ' > [data-m="sai-home-cover"] { display: block; position: absolute; inset: 0; z-index: 2; cursor: pointer; background: #141412; }' +
    HOME + ' > [data-m="sai-home-cover"] img { width: 100%; height: 100%; object-fit: cover; display: block; }' +
    HOME + ' [data-m="sai-body"] > div:nth-child(3) { position: absolute !important; left: 5%; right: 5%; bottom: 3.5%; z-index: 3; margin: 0 !important; background: rgba(237,232,216,.97); box-shadow: 0 6px 18px rgba(0,0,0,.45); }' +
    // on a phone the box keeps its head and button only, so it covers less art
    '@media (max-width: 900px) { ' + HOME + ' [data-m="sai-body"] > div:nth-child(3) > div:first-child > div:nth-child(2) { display: none !important; } ' +
    HOME + ' [data-m="sai-body"] > div:nth-child(3) > div:first-child { padding: 8px 10px !important; } }';
  (document.head || document.documentElement).appendChild(homeCss);

  var homeCover = null;
  var homeHost = null;
  var homeObs = null;

  function ensureHomeCover(c) {
    var el = c && c.el;
    if (!el || !el.matches(HOME)) return;
    var art = document.querySelector('#store img[src*="sai-cover"]:not([data-m-home])');
    var src = art ? art.src : (homeCover ? homeCover.firstChild.src : '');
    if (!src) return;
    if (!homeCover) {
      homeCover = el.ownerDocument.createElement('div');
      homeCover.setAttribute('data-m', 'sai-home-cover');
      homeCover.setAttribute('role', 'link');
      homeCover.tabIndex = 0;
      var img = document.createElement('img');
      img.setAttribute('data-m-home', '1');
      img.alt = 'The Southern Air Import, November 1986: open the issue';
      homeCover.appendChild(img);
      var open = function () {
        var btn = homeHost && homeHost.querySelector('[data-m="sai-body"] > div:nth-child(3) [tabindex]');
        if (btn) btn.click();
      };
      homeCover.addEventListener('click', open);
      homeCover.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } });
    }
    if (homeCover.firstChild.src !== src) homeCover.firstChild.src = src;
    if (homeHost !== el) {
      if (homeObs) homeObs.disconnect();
      homeHost = el;
      homeObs = new MutationObserver(function () {
        if (homeHost && homeHost.isConnected && homeCover.parentElement !== homeHost) homeHost.appendChild(homeCover);
      });
      homeObs.observe(el, { childList: true });
    }
    if (homeCover.parentElement !== el) el.appendChild(homeCover);
  }

  function putAdded() {
    tocAdded.forEach(function (n) { if (n.parentElement !== tocHost) tocHost.appendChild(n); });
  }

  function tick(now) {
    if (!window.SAT_ISSUE || !window.SAT_ISSUE._sheeted) paginate();
    cur = current();
    if (cur) {
      var resized = window.innerWidth !== lastW || window.innerHeight !== lastH;
      if (resized) sheetPx = null;
      var turned = cur.key !== lastKey;
      if (turned) {
        trim = 1;
        hovering = false;
        remeasureAt = REMEASURE_MS.map(function (ms) { return now + ms; });
        needScroll = true;
      }
      var due = remeasureAt.length && now >= remeasureAt[0];
      if (due) remeasureAt.shift();

      ensureToc(cur);
      ensureHomeCover(cur);
      if (turned || resized || due || target !== cur.el) {
        rcLayout(cur, due);
        featureFill(cur, true);
        innerScale = measureInner(cur);
        scale = !canScale(cur) ? 1 : Math.max(MIN_SCALE, measure(cur) * trim);
        lastKey = cur.key;
        lastW = window.innerWidth;
        lastH = window.innerHeight;
        target = cur.el;
        verify = 6;
      }

      if (cur.inner && !isPhone()) {
        if (rcOwns(cur)) { if (!rcMark()) rcLayout(cur, false); } else { assignSizes(cur.el); }
        featureFill(cur, false);
      }
      if (cur.inner) {
        var wantInner = zoomStr(innerScale);
        if (cur.inner.style.zoom !== wantInner) cur.inner.style.zoom = wantInner;
      }
      var want = canScale(cur) ? zoomStr(scale) : '';
      if (cur.el.style.zoom !== want) cur.el.style.zoom = want;
      // The runtime shows the next sheet, and rewrites every sheet's style
      // attribute on each render, before this loop runs again: for that one
      // frame the inline zoom is gone and the sheet flashed up at full size
      // (merchant: "it loads the entire page zoomed in for half a
      // millisecond"). The last fitted zoom also stands in a stylesheet rule,
      // which the inline value overrides once it is back.
      if (cur.kind !== 'cover') {
        var zoomRule = canScale(cur) ? '@media (min-width: 901px) { ' + P + ' { zoom: ' + (want || '1') + '; } }' : '';
        if (zoomCss.textContent !== zoomRule) zoomCss.textContent = zoomRule;
      }

      // The reserve above is an estimate (margins collapse differently once the
      // page is zoomed), so check where the sheet and its controls really end
      // up and trim. Covers skip this: they copy the settled page size.
      if (verify > 0 && want && !(cur.kind === 'cover' && sheetPx)) {
        verify--;
        var used = cur.last.getBoundingClientRect().bottom - cur.anchor.getBoundingClientRect().top;
        var over = used - (window.innerHeight - (isPhone() ? 24 : EDGE_TOP + EDGE_BOTTOM));
        var h = cur.el.getBoundingClientRect().height;
        if (over > 1 && h > over && scale > MIN_SCALE) {
          trim *= (h - over) / h;
          scale = Math.max(MIN_SCALE, scale * (h - over) / h);
          needScroll = true;
        } else if (over <= 1) {
          verify = 0;
          if (cur.kind !== 'cover') sheetPx = Math.round(h);
        }
      }

      var top = cur.anchor.getBoundingClientRect().top + window.pageYOffset - (isPhone() ? 12 : EDGE_TOP);
      sheetScrollY = isPhone() ? null : Math.max(0, Math.round(top));
      if (needScroll) {
        needScroll = false;
        if (Math.abs(window.pageYOffset - top) > 4) window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
      }
      applyMagnifier(cur);
    } else {
      lastKey = null;
      sheetScrollY = null;
      target = null;
      hovering = false;
      applyMagnifier(null);
      rcClear();
    }
    updateControls(cur);
    requestAnimationFrame(tick);
  }

  requestAnimationFrame(tick);
})();
